import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Sun, Moon, Download, Upload, Printer, ChevronDown, ChevronUp } from 'lucide-react';

// --- Helper Functions ---
const formatCurrency = (value, decimals = 0) => {
    if (isNaN(value) || value === null) return 'N/A';
    const options = { style: 'currency', currency: 'USD', minimumFractionDigits: decimals, maximumFractionDigits: decimals };
    return new Intl.NumberFormat('en-US', options).format(value);
};

const formatPercentage = (value) => {
    if (isNaN(value) || value === null) return 'N/A';
    return `${(Number(value) * 100).toFixed(2)}%`;
};

const formatYears = (value) => {
    if (isNaN(value) || value === null || value === Infinity) return 'N/A';
    return `${value.toFixed(2)} years`;
}

// --- Financial Calculation Engine ---
const calculateFinancials = (inputs) => {

    const calculateInvestmentAndFinancing = () => {
        const totalInvestmentEstimate = inputs.installedCapacity * inputs.investmentPerMW;
        const capitalFundRatio = totalInvestmentEstimate > 0 ? inputs.ownFunds / totalInvestmentEstimate : 0;
        const loanRatio = 1 - capitalFundRatio;
        const constructionLoan = totalInvestmentEstimate * loanRatio;

        const equipmentPurchase = totalInvestmentEstimate * 0.7;
        const installationProject = totalInvestmentEstimate * 0.15;
        const otherConstructionCosts = totalInvestmentEstimate * 0.1;
        const preparatoryWork = totalInvestmentEstimate * 0.05;
        const totalStaticInvestment = equipmentPurchase + installationProject + otherConstructionCosts + preparatoryWork;
        const interestDuringConstruction = constructionLoan * inputs.longTermLoanRate * inputs.constructionPeriod;
        const workingCapital = totalInvestmentEstimate * 0.02;
        const totalInvestment = totalStaticInvestment + interestDuringConstruction + workingCapital;
        
        const capital = inputs.ownFunds;
        const loan = totalInvestment - capital;
        const totalFinancing = capital + loan;

        return {
            totalInvestment,
            totalStaticInvestment,
            workingCapital,
            financing: { capital, loan, totalFinancing },
            loanDetails: { constructionLoan, capitalFundRatio },
            interestDuringConstruction
        };
    };

    const calculateLoanRepayment = (loanAmount, loanRate, term) => {
        const schedule = [];
        let remainingLoan = loanAmount;
        const annualPrincipalPayment = term > 0 ? loanAmount / term : 0;

        for (let year = 1; year <= term; year++) {
            if (remainingLoan <= 0.01) {
                schedule.push({ year, principal: 0, interest: 0, totalPayment: 0, remainingLoan: 0 });
                continue;
            }
            const interestPayment = remainingLoan * loanRate;
            const principalPayment = Math.min(remainingLoan, annualPrincipalPayment);
            const totalPayment = principalPayment + interestPayment;
            remainingLoan -= principalPayment;
            schedule.push({ year, principal: principalPayment, interest: interestPayment, totalPayment, remainingLoan });
        }
        return schedule;
    };
    
    const calculateIndicators = (projectCF, capitalCF, discountRate, totalInvestment, costStatement, pnlStatement, depreciableBase) => {
        const calculateIRR = (cashflows, maxIterations = 100, tolerance = 1e-7) => {
            let guess = 0.1;
            for (let i = 0; i < maxIterations; i++) {
                const currentGuess = guess;
                const npv = cashflows.reduce((acc, val, j) => acc + val / Math.pow(1 + currentGuess, j), 0);
                const derivative = cashflows.reduce((acc, val, j) => acc - j * val / Math.pow(1 + currentGuess, j + 1), 0);
                if (Math.abs(derivative) < tolerance) break;
                const newGuess = guess - npv / derivative;
                if (isNaN(newGuess) || !isFinite(newGuess)) break;
                if (Math.abs(newGuess - guess) < tolerance) return newGuess;
                guess = newGuess;
            }
            return isNaN(guess) ? 0 : guess;
        };

        const projectNPV = projectCF.reduce((acc, val, i) => acc + val / Math.pow(1 + discountRate, i), 0);
        const capitalNPV = capitalCF.reduce((acc, val, i) => acc + val / Math.pow(1 + discountRate, i), 0);
        const projectIRR = calculateIRR(projectCF);
        const capitalIRR = calculateIRR(capitalCF);

        let cumulativeCF = 0;
        let paybackPeriod = Infinity;
        for(let i = 1; i < projectCF.length; i++) {
            cumulativeCF += projectCF[i];
            if(cumulativeCF >= -projectCF[0]) {
                paybackPeriod = (i - 1) + (-projectCF[0] - (cumulativeCF - projectCF[i])) / projectCF[i];
                break;
            }
        }
        
        let cumulativeDiscountedCF = 0;
        let discountedPaybackPeriod = Infinity;
        for(let i = 1; i < projectCF.length; i++) {
            const discountedCF = projectCF[i] / Math.pow(1 + discountRate, i);
            cumulativeDiscountedCF += discountedCF;
            if(cumulativeDiscountedCF >= -projectCF[0]) {
                 const prevCumulativeDiscountedCF = cumulativeDiscountedCF - discountedCF;
                 discountedPaybackPeriod = (i - 1) + (-projectCF[0] - prevCumulativeDiscountedCF) / discountedCF;
                 break;
            }
        }

        // LCOE Calculation
        const sumDiscountedOandM = costStatement.reduce((acc, cost) => {
            return acc + (cost.operatingCost + cost.landCost) / Math.pow(1 + discountRate, cost.year);
        }, 0);
        
        const salvageValue = depreciableBase * inputs.salvageValueRate;
        const discountedSalvageValue = salvageValue / Math.pow(1 + discountRate, inputs.operationPeriod);

        const totalDiscountedCosts = totalInvestment + sumDiscountedOandM - discountedSalvageValue;

        const discountedEnergy = pnlStatement.reduce((acc, pnl) => {
            return acc + pnl.annualGeneration / Math.pow(1 + discountRate, pnl.year);
        }, 0);

        const lcoe = discountedEnergy > 0 ? totalDiscountedCosts / discountedEnergy : 0;

        return { projectNPV, capitalNPV, projectIRR, capitalIRR, paybackPeriod, discountedPaybackPeriod, lcoe };
    };

    const outputs = {};
    const { totalInvestment, totalStaticInvestment, workingCapital, financing, loanDetails, interestDuringConstruction } = calculateInvestmentAndFinancing();
    outputs.investmentPlan = { totalInvestment, totalStaticInvestment, workingCapital, ...loanDetails, interestDuringConstruction };
    outputs.financing = financing;

    const depreciableBase = totalStaticInvestment + interestDuringConstruction;
    const annualDepreciation = inputs.depreciationPeriod > 0 ? (depreciableBase * (1 - inputs.salvageValueRate)) / inputs.depreciationPeriod : 0;
    outputs.loanRepaymentSchedule = calculateLoanRepayment(financing.loan, inputs.longTermLoanRate, inputs.operationPeriod);

    const pnlStatement = [], costStatement = [], cashFlowStatement = [], balanceSheet = [], evaStatement = [];
    let accumulatedRetainedEarnings = 0;
    let accumulatedStatutoryReserve = 0;
    let accumulatedDiscretionaryReserve = 0;

    for (let year = 1; year <= inputs.operationPeriod; year++) {
        const pnl = {}, cost = {}, cf = {}, balance = {}, eva = {};
        const degradationFactor = Math.pow((1 - inputs.pvDegradationRate), year - 1);
        const annualGeneration = (inputs.installedCapacity * 1000 * inputs.annualHours) * degradationFactor;
        const loanInterest = outputs.loanRepaymentSchedule[year - 1]?.interest || 0;

        pnl.year = year;
        pnl.annualGeneration = annualGeneration;
        pnl.operatingIncome = annualGeneration * inputs.onGridTariff;
        
        cost.year = year;
        
        // Calculate land costs (annual lease/rent)
        const totalAcres = inputs.installedCapacity * inputs.acresPerMW;
        const annualLandCost = totalAcres * inputs.landCostPerAcre;
        
        if (year === 1) {
            cost.operatingCost = pnl.operatingIncome * inputs.operatingCostRate;
        } else {
            const previousYearCost = costStatement[costStatement.length - 1].operatingCost;
            cost.operatingCost = previousYearCost * (1 + inputs.operatingCostEscalationRate);
        }
        
        cost.landCost = annualLandCost;

        cost.depreciation = year <= inputs.depreciationPeriod ? annualDepreciation : 0;
        cost.interest = loanInterest;
        cost.totalCost = cost.operatingCost + cost.depreciation + cost.interest + cost.landCost;
        cost.costPerKwh = annualGeneration > 0 ? cost.totalCost / annualGeneration : 0;
        
        pnl.vatSurtax = pnl.operatingIncome * inputs.vatRate * inputs.surtaxRate;
        pnl.totalProfit = pnl.operatingIncome - cost.totalCost - pnl.vatSurtax;
        pnl.incomeTax = pnl.totalProfit > 0 ? pnl.totalProfit * inputs.incomeTaxRate : 0;
        pnl.netProfit = pnl.totalProfit - pnl.incomeTax;
        
        const statutoryReserve = pnl.netProfit > 0 ? pnl.netProfit * inputs.statutoryReserveRate : 0;
        const discretionaryReserve = pnl.netProfit > 0 ? pnl.netProfit * inputs.discretionaryReserveRate : 0;
        const profitAvailableForDistribution = pnl.netProfit - statutoryReserve - discretionaryReserve;
        pnl.profitDistribution = profitAvailableForDistribution > 0 ? profitAvailableForDistribution * inputs.profitDistributionRate : 0;
        pnl.retainedEarnings = profitAvailableForDistribution - pnl.profitDistribution;
        
        accumulatedStatutoryReserve += statutoryReserve;
        accumulatedDiscretionaryReserve += discretionaryReserve;
        accumulatedRetainedEarnings += pnl.retainedEarnings;

        cf.year = year;
        cf.netCashFlow_op = pnl.netProfit + cost.depreciation;
        
        balance.year = year;
        const fixedAssets = Math.max(0, depreciableBase - (annualDepreciation * Math.min(year, inputs.depreciationPeriod)));
        const cashFromOps = (balanceSheet[year-2]?.currentAssets || workingCapital) + cf.netCashFlow_op;
        const financingActivities = - (outputs.loanRepaymentSchedule[year-1]?.principal || 0) - pnl.profitDistribution;
        balance.currentAssets = cashFromOps + financingActivities;
        balance.totalAssets = fixedAssets + balance.currentAssets;
        
        balance.longTermDebt = outputs.loanRepaymentSchedule[year - 1]?.remainingLoan || 0;
        balance.currentLiabilities = 0; 
        balance.totalLiabilities = balance.longTermDebt + balance.currentLiabilities;
        
        balance.equity = financing.capital;
        balance.accumulatedProfit = accumulatedRetainedEarnings;
        balance.totalEquity = balance.equity + accumulatedRetainedEarnings + accumulatedStatutoryReserve + accumulatedDiscretionaryReserve;
        balance.totalLiabilitiesAndEquity = balance.totalLiabilities + balance.totalEquity;
        balance.check = balance.totalAssets - balance.totalLiabilitiesAndEquity;

        const nopat = pnl.netProfit + loanInterest * (1 - inputs.incomeTaxRate);
        eva.year = year;
        const capitalEmployed = (balanceSheet[year-2]?.totalAssets || totalInvestment) - (balanceSheet[year-2]?.currentLiabilities || 0);
        eva.eva = nopat - capitalEmployed * inputs.discountRate;

        pnlStatement.push(pnl); costStatement.push(cost); cashFlowStatement.push(cf); balanceSheet.push(balance); evaStatement.push(eva);
    }
    
    outputs.pnlStatement = pnlStatement; outputs.costStatement = costStatement; outputs.cashFlowStatement = cashFlowStatement; outputs.balanceSheet = balanceSheet; outputs.evaStatement = evaStatement;
    
    const projectCF = [-totalInvestment, ...pnlStatement.map(p => p.netProfit + (p.interest || 0) * (1 - inputs.incomeTaxRate) + (costStatement.find(c=>c.year===p.year)?.depreciation || 0))];
    const capitalCF = [-financing.capital, ...pnlStatement.map(p => p.profitDistribution)];
    outputs.indicators = calculateIndicators(projectCF, capitalCF, inputs.discountRate, totalInvestment, costStatement, pnlStatement, depreciableBase);
    
    return outputs;
};


// --- UI Components ---
const Tab = ({ label, isActive, onClick }) => (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all duration-200 focus:outline-none ${isActive ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
        {label}
    </button>
);

const InputCell = ({ id, value, onChange, label, unit, isPercentage = false, step = "any" }) => (
    <div className="grid grid-cols-2 gap-4 items-center py-2 px-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-md">
        <label htmlFor={id} className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
        <div className="flex items-center">
            <input 
                type="number" 
                id={id} 
                value={isPercentage ? value * 100 : value} 
                onChange={e => onChange(id, isPercentage ? e.target.value / 100 : e.target.value)} 
                className="w-full px-3 py-1.5 text-right bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-gray-100"
                step={step}
            />
            <span className="text-sm text-gray-500 dark:text-gray-400 w-24 text-right pr-2">{unit}</span>
        </div>
    </div>
);

const OutputCell = ({ label, value, unit, formatter = (v) => formatCurrency(v) }) => (
    <div className="grid grid-cols-2 gap-4 items-center py-2 px-3">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <div className="flex justify-end items-center space-x-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-md w-full text-right">{formatter(value)}</span>
            {unit && <span className="text-sm text-gray-500 dark:text-gray-400 w-24">{unit}</span>}
        </div>
    </div>
);

const SectionHeader = ({ title }) => (
    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 bg-gray-100 dark:bg-gray-700/50 p-3 my-4 rounded-md">{title}</h3>
);

const Explanation = ({ title, children }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    return (
        <div className="mt-8 border-t-2 border-gray-200 dark:border-gray-700 explanation-section">
            <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
            >
                <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">{title}</h4>
                {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                )}
            </button>
            {isExpanded && (
                <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400 space-y-4 animate-fade-in">
                    {children}
                </div>
            )}
        </div>
    );
};

// --- Main App Component ---
export default function App() {
    const [activeTab, setActiveTab] = useState('Summary');
    const [isLoadingGoalSeek, setIsLoadingGoalSeek] = useState(false);
    const [goalSeekMessage, setGoalSeekMessage] = useState('');
    const [isDarkMode, setIsDarkMode] = useState(true);
    const fileInputRef = React.useRef(null);

    const [inputs, setInputs] = useState({
        constructionPeriod: 1, operationPeriod: 25, installedCapacity: 10,
        investmentPerMW: 500000, annualHours: 1200, onGridTariff: 0.09,
        pvDegradationRate: 0.004,
        operatingCostRate: 0.10, operatingCostEscalationRate: 0.02,
        ownFunds: 1250000, longTermLoanRate: 0.0475,
        depreciationPeriod: 25, salvageValueRate: 0.05, vatRate: 0.13,
        surtaxRate: 0.12, incomeTaxRate: 0.25, statutoryReserveRate: 0.10,
        discretionaryReserveRate: 0.05, profitDistributionRate: 0.80,
        discountRate: 0.06, goalSeekTargetIRR: 0.08,
        landCostPerAcre: 1000, acresPerMW: 5,
    });

    const [outputs, setOutputs] = useState({});

    useEffect(() => {
        setOutputs(calculateFinancials(inputs));
    }, [inputs]);

    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [isDarkMode]);

    const handleInputChange = (id, value) => {
        setInputs(prev => ({ ...prev, [id]: Number(value) }));
    };

    const handleExport = () => {
        const header = Object.keys(inputs).join(',');
        const values = Object.values(inputs).join(',');
        const csvContent = `${header}\n${values}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'solar_model_parameters.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleImportClick = () => {
        fileInputRef.current.click();
    };

    const handleImport = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target.result;
                const lines = text.split('\n');
                if (lines.length >= 2) {
                    const header = lines[0].split(',');
                    const values = lines[1].split(',');
                    const newInputs = {};
                    header.forEach((key, index) => {
                        newInputs[key.trim()] = Number(values[index]);
                    });
                    setInputs(prev => ({...prev, ...newInputs}));
                }
            };
            reader.readAsText(file);
        }
    };

    const handlePrint = async () => {
        const printableTabs = tabs.filter(tab => tab !== 'README');
        
        let allTabsContent = '';
        
        // Generate content for each tab
        for (let i = 0; i < printableTabs.length; i++) {
            const tab = printableTabs[i];
            try {
                let tabHtml = '';
                
                // Render tab content without Explanation components
                switch (tab) {
                    case 'Summary':
                        tabHtml = `
                            <div class="print-page">
                                <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                <div class="summary-content">
                                    <h2 class="text-2xl font-bold text-gray-800 mb-4">Financial Indicators Summary</h2>
                                    <div class="grid md:grid-cols-2 gap-6">
                                        <div class="bg-white p-4 rounded-lg border border-gray-200">
                                            <h3 class="font-semibold text-lg mb-3 text-blue-700">Project Investment Analysis</h3>
                                            <div class="space-y-2">
                                                <div class="flex justify-between"><span>Total Investment:</span><span>${formatCurrency(outputs.investmentPlan?.totalInvestment)}</span></div>
                                                <div class="flex justify-between"><span>Project NPV:</span><span>${formatCurrency(outputs.indicators?.projectNPV)}</span></div>
                                                <div class="flex justify-between"><span>Project IRR (after tax):</span><span>${formatPercentage(outputs.indicators?.projectIRR)}</span></div>
                                                <div class="flex justify-between"><span>Payback Period:</span><span>${formatYears(outputs.indicators?.paybackPeriod)}</span></div>
                                                <div class="flex justify-between"><span>Discounted Payback Period:</span><span>${formatYears(outputs.indicators?.discountedPaybackPeriod)}</span></div>
                                            </div>
                                        </div>
                                        <div class="bg-white p-4 rounded-lg border border-gray-200">
                                            <h3 class="font-semibold text-lg mb-3 text-green-700">Capital Analysis</h3>
                                            <div class="space-y-2">
                                                <div class="flex justify-between"><span>Capital NPV:</span><span>${formatCurrency(outputs.indicators?.capitalNPV)}</span></div>
                                                <div class="flex justify-between"><span>Capital IRR (after tax):</span><span>${formatPercentage(outputs.indicators?.capitalIRR)}</span></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6">
                                        <h3 class="font-semibold text-lg mb-3 text-purple-700">Key Metrics</h3>
                                        <div class="grid md:grid-cols-2 gap-x-6 gap-y-4">
                                            <div class="flex justify-between"><span>LCOE:</span><span>${formatCurrency(outputs.indicators?.lcoe, 4)}/kWh</span></div>
                                            <div class="flex justify-between"><span>Avg. Cost per kWh:</span><span>${formatCurrency(outputs.costStatement?.reduce((a,b) => a + b.costPerKwh, 0) / outputs.costStatement?.length, 4)}/kWh</span></div>
                                            <div class="flex justify-between"><span>Total Net Profit:</span><span>${formatCurrency(outputs.pnlStatement?.reduce((a,b) => a + b.netProfit, 0))}</span></div>
                                                                                         <div class="flex justify-between"><span>Total EVA:</span><span>${formatCurrency(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0))}</span></div>
                                        </div>
                                    </div>
                                    
                                    <div class="mt-8">
                                        <h2 class="text-xl font-bold mb-4">Key Performance Indicators (KPIs)</h2>
                                        <div class="grid md:grid-cols-3 gap-6">
                                            <div>
                                                <h3 class="text-lg font-semibold mb-3 text-blue-700">Investment Viability</h3>
                                                <div class="space-y-2">
                                                    <div class="flex justify-between"><span>Project IRR vs Discount:</span><span>${formatPercentage(outputs.indicators?.projectIRR)} vs ${formatPercentage(inputs.discountRate)}</span></div>
                                                    <div class="flex justify-between"><span>Project NPV:</span><span>${formatCurrency(outputs.indicators?.projectNPV)}</span></div>
                                                    <div class="flex justify-between"><span>LCOE:</span><span>${formatCurrency(outputs.indicators?.lcoe, 4)}/kWh</span></div>
                                                    <div class="flex justify-between"><span>Payback Period:</span><span>${formatYears(outputs.indicators?.paybackPeriod)}</span></div>
                                                </div>
                                            </div>
                                            <div>
                                                <h3 class="text-lg font-semibold mb-3 text-green-700">Investor Returns</h3>
                                                <div class="space-y-2">
                                                    <div class="flex justify-between"><span>Capital IRR:</span><span>${formatPercentage(outputs.indicators?.capitalIRR)}</span></div>
                                                    <div class="flex justify-between"><span>Capital NPV:</span><span>${formatCurrency(outputs.indicators?.capitalNPV)}</span></div>
                                                    <div class="flex justify-between"><span>Profit Distribution:</span><span>${formatPercentage(inputs.profitDistributionRate)}</span></div>
                                                    <div class="flex justify-between"><span>Total EVA:</span><span>${formatCurrency(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0)}</span></div>
                                                </div>
                                            </div>
                                            <div>
                                                <h3 class="text-lg font-semibold mb-3 text-purple-700">Financial Health</h3>
                                                <div class="space-y-2">
                                                    <div class="flex justify-between"><span>Operating Margin:</span><span>${formatPercentage(1 - inputs.operatingCostRate)}</span></div>
                                                    <div class="flex justify-between"><span>Cost Escalation:</span><span>${formatPercentage(inputs.operatingCostEscalationRate)}</span></div>
                                                    <div class="flex justify-between"><span>Viability Status:</span><span>${(outputs.indicators?.projectIRR || 0) > inputs.discountRate ? 'VIABLE' : 'NOT VIABLE'}</span></div>
                                                    <div class="flex justify-between"><span>NPV Status:</span><span>${(outputs.indicators?.projectNPV || 0) > 0 ? 'POSITIVE' : 'NEGATIVE'}</span></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        `;
                        break;

                    case 'Basic Data':
                        tabHtml = `
                            <div class="print-page">
                                <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                <div class="grid lg:grid-cols-2 gap-8">
                                    <div class="bg-white p-6 rounded-lg border border-gray-200">
                                        <h2 class="text-xl font-semibold mb-4 text-blue-700">Project Configuration</h2>
                                        <div class="space-y-3">
                                            <div class="flex justify-between"><span>Construction Period:</span><span>${inputs.constructionPeriod} years</span></div>
                                            <div class="flex justify-between"><span>Operation Period:</span><span>${inputs.operationPeriod} years</span></div>
                                            <div class="flex justify-between"><span>Installed Capacity:</span><span>${inputs.installedCapacity} MW</span></div>
                                            <div class="flex justify-between"><span>Investment per MW:</span><span>${formatCurrency(inputs.investmentPerMW)}</span></div>
                                            <div class="flex justify-between"><span>Own Funds:</span><span>${formatCurrency(inputs.ownFunds)}</span></div>
                                            <div class="flex justify-between"><span>Acres per MW:</span><span>${inputs.acresPerMW}</span></div>
                                        </div>
                                    </div>
                                    <div class="bg-white p-6 rounded-lg border border-gray-200">
                                        <h2 class="text-xl font-semibold mb-4 text-green-700">Revenue & Cost Parameters</h2>
                                        <div class="space-y-3">
                                            <div class="flex justify-between"><span>Annual Hours:</span><span>${inputs.annualHours} hours</span></div>
                                            <div class="flex justify-between"><span>On-Grid Tariff:</span><span>${formatCurrency(inputs.onGridTariff, 4)}/kWh</span></div>
                                            <div class="flex justify-between"><span>PV Degradation Rate:</span><span>${formatPercentage(inputs.pvDegradationRate)}</span></div>
                                            <div class="flex justify-between"><span>Operating Cost Rate:</span><span>${formatPercentage(inputs.operatingCostRate)}</span></div>
                                            <div class="flex justify-between"><span>Operating Cost Escalation:</span><span>${formatPercentage(inputs.operatingCostEscalationRate)}</span></div>
                                            <div class="flex justify-between"><span>Land Cost per Acre:</span><span>${formatCurrency(inputs.landCostPerAcre)}</span></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        `;
                        break;

                    case 'Investment & Financing':
                        if (outputs.investmentPlan) {
                            tabHtml = `
                                <div class="print-page">
                                    <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                    <div class="space-y-6">
                                        <div>
                                            <h2 class="text-xl font-semibold mb-4">Investment Plan</h2>
                                            <div class="space-y-2">
                                                <div class="flex justify-between"><span>Total Investment:</span><span>${formatCurrency(outputs.investmentPlan.totalInvestment)}</span></div>
                                                <div class="flex justify-between"><span>Total Static Investment:</span><span>${formatCurrency(outputs.investmentPlan.totalStaticInvestment)}</span></div>
                                                <div class="flex justify-between"><span>Working Capital:</span><span>${formatCurrency(outputs.investmentPlan.workingCapital)}</span></div>
                                            </div>
                                        </div>
                                        <div>
                                            <h2 class="text-xl font-semibold mb-4">Financing Structure</h2>
                                            <div class="space-y-2">
                                                <div class="flex justify-between"><span>Capital (Equity):</span><span>${formatCurrency(outputs.investmentPlan.financing.capital)}</span></div>
                                                <div class="flex justify-between"><span>Loan:</span><span>${formatCurrency(outputs.investmentPlan.financing.loan)}</span></div>
                                                <div class="flex justify-between"><span>Total Financing:</span><span>${formatCurrency(outputs.investmentPlan.financing.totalFinancing)}</span></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            `;
                        }
                        break;

                    case 'Loan Repayment':
                        if (outputs.loanRepayment && outputs.loanRepayment.length > 0) {
                            const tableRows = outputs.loanRepayment.slice(0, 15).map(row => 
                                `<tr>
                                    <td class="border px-2 py-1">${row.year}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.openingBalance, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.interest, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.principal, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.closingBalance, 0)}</td>
                                </tr>`
                            ).join('');
                            
                            tabHtml = `
                                <div class="print-page">
                                    <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                    <table class="w-full text-sm border-collapse border">
                                        <thead>
                                            <tr class="bg-gray-100">
                                                <th class="border px-2 py-1">Year</th>
                                                <th class="border px-2 py-1">Opening Balance</th>
                                                <th class="border px-2 py-1">Interest</th>
                                                <th class="border px-2 py-1">Principal</th>
                                                <th class="border px-2 py-1">Closing Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody>${tableRows}</tbody>
                                    </table>
                                </div>
                            `;
                        }
                        break;

                    case 'Total Cost & Cost/kWh':
                        if (outputs.costStatement && outputs.costStatement.length > 0) {
                            const tableRows = outputs.costStatement.slice(0, 15).map(row => 
                                `<tr>
                                    <td class="border px-2 py-1">${row.year}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.operatingCost, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.landCost, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.depreciation, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.costPerKwh, 4)}</td>
                                </tr>`
                            ).join('');
                            
                            tabHtml = `
                                <div class="print-page">
                                    <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                    <table class="w-full text-sm border-collapse border">
                                        <thead>
                                            <tr class="bg-gray-100">
                                                <th class="border px-2 py-1">Year</th>
                                                <th class="border px-2 py-1">Operating Cost</th>
                                                <th class="border px-2 py-1">Land Cost</th>
                                                <th class="border px-2 py-1">Depreciation</th>
                                                <th class="border px-2 py-1">Cost per kWh</th>
                                            </tr>
                                        </thead>
                                        <tbody>${tableRows}</tbody>
                                    </table>
                                </div>
                            `;
                        }
                        break;

                    case 'Profit & Loss':
                        if (outputs.pnlStatement && outputs.pnlStatement.length > 0) {
                            const tableRows = outputs.pnlStatement.slice(0, 15).map(row => 
                                `<tr>
                                    <td class="border px-2 py-1">${row.year}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.operatingIncome, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.operatingCost, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.netProfit, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.totalProfit, 0)}</td>
                                </tr>`
                            ).join('');
                            
                            tabHtml = `
                                <div class="print-page">
                                    <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                    <table class="w-full text-sm border-collapse border">
                                        <thead>
                                            <tr class="bg-gray-100">
                                                <th class="border px-2 py-1">Year</th>
                                                <th class="border px-2 py-1">Operating Income</th>
                                                <th class="border px-2 py-1">Operating Cost</th>
                                                <th class="border px-2 py-1">Net Profit</th>
                                                <th class="border px-2 py-1">Total Profit</th>
                                            </tr>
                                        </thead>
                                        <tbody>${tableRows}</tbody>
                                    </table>
                                </div>
                            `;
                        }
                        break;

                    case 'Balance Sheet':
                        if (outputs.balanceSheet && outputs.balanceSheet.length > 0) {
                            const tableRows = outputs.balanceSheet.slice(0, 15).map(row => 
                                `<tr>
                                    <td class="border px-2 py-1">${row.year}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.equity, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.longTermLoan, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.netFixedAssets, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.check, 0)}</td>
                                </tr>`
                            ).join('');
                            
                            tabHtml = `
                                <div class="print-page">
                                    <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                    <table class="w-full text-sm border-collapse border">
                                        <thead>
                                            <tr class="bg-gray-100">
                                                <th class="border px-2 py-1">Year</th>
                                                <th class="border px-2 py-1">Equity</th>
                                                <th class="border px-2 py-1">Long Term Loan</th>
                                                <th class="border px-2 py-1">Net Fixed Assets</th>
                                                <th class="border px-2 py-1">Balance Check</th>
                                            </tr>
                                        </thead>
                                        <tbody>${tableRows}</tbody>
                                    </table>
                                </div>
                            `;
                        }
                        break;

                    case 'EVA':
                        if (outputs.evaStatement && outputs.evaStatement.length > 0) {
                            const tableRows = outputs.evaStatement.slice(0, 15).map(row => 
                                `<tr>
                                    <td class="border px-2 py-1">${row.year}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.nopat, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.investedCapital, 0)}</td>
                                    <td class="border px-2 py-1">${formatPercentage(row.wacc)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.eva, 0)}</td>
                                </tr>`
                            ).join('');
                            
                            tabHtml = `
                                <div class="print-page">
                                    <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                    <table class="w-full text-sm border-collapse border">
                                        <thead>
                                            <tr class="bg-gray-100">
                                                <th class="border px-2 py-1">Year</th>
                                                <th class="border px-2 py-1">NOPAT</th>
                                                <th class="border px-2 py-1">Invested Capital</th>
                                                <th class="border px-2 py-1">WACC</th>
                                                <th class="border px-2 py-1">EVA</th>
                                            </tr>
                                        </thead>
                                        <tbody>${tableRows}</tbody>
                                    </table>
                                </div>
                            `;
                        }
                        break;

                    case 'Charts':
                        tabHtml = `
                            <div class="print-page">
                                <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                <p class="text-gray-600 mb-4">Financial charts visualization summary:</p>
                                <div class="space-y-4">
                                    <div>
                                        <h3 class="text-lg font-semibold mb-2">Profitability Trends</h3>
                                        <p class="text-sm text-gray-600">Operating Income and Net Profit trends over the project lifecycle.</p>
                                    </div>
                                    <div>
                                        <h3 class="text-lg font-semibold mb-2">Cost Structure Analysis</h3>
                                        <p class="text-sm text-gray-600">Breakdown of operating costs, land costs, depreciation, and interest over time.</p>
                                    </div>
                                    <div class="mt-6">
                                        <h3 class="text-lg font-semibold mb-2">Key Financial Indicators Summary</h3>
                                        <div class="grid md:grid-cols-2 gap-4">
                                            <div class="space-y-2">
                                                <div class="flex justify-between"><span>Project NPV:</span><span>${formatCurrency(outputs.indicators?.projectNPV)}</span></div>
                                                <div class="flex justify-between"><span>Project IRR:</span><span>${formatPercentage(outputs.indicators?.projectIRR)}</span></div>
                                                <div class="flex justify-between"><span>Capital IRR:</span><span>${formatPercentage(outputs.indicators?.capitalIRR)}</span></div>
                                            </div>
                                            <div class="space-y-2">
                                                <div class="flex justify-between"><span>LCOE:</span><span>${formatCurrency(outputs.indicators?.lcoe, 4)}/kWh</span></div>
                                                <div class="flex justify-between"><span>Payback Period:</span><span>${formatYears(outputs.indicators?.paybackPeriod)}</span></div>
                                                <div class="flex justify-between"><span>Total EVA:</span><span>${formatCurrency(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0)}</span></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        `;
                        break;

                    default:
                        tabHtml = `
                            <div class="print-page">
                                <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                <p class="text-gray-600">Content for ${tab} tab - refer to the digital version for full interactive content.</p>
                            </div>
                        `;
                        break;
                }
                
                allTabsContent += tabHtml;
            } catch (error) {
                console.error(`Error rendering ${tab}:`, error);
                allTabsContent += `
                    <div class="print-page">
                        <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                        <p class="text-gray-600">Error loading content for ${tab}</p>
                    </div>
                `;
            }
        }

        // Create print frame
        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'absolute';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = '0';
        
        document.body.appendChild(printFrame);
        
        const frameDoc = printFrame.contentWindow.document;
        frameDoc.open();
        frameDoc.write(`
            <html>
                <head>
                    <title>Solar Financial Model - Complete Report</title>
                    <style>
                        * { margin: 0; padding: 0; box-sizing: border-box; }
                        body { font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; color: #333; }
                        .print-page { page-break-after: always; padding: 20px; }
                        .print-page:last-child { page-break-after: auto; }
                        h1 { font-size: 24px; margin-bottom: 20px; color: #1f2937; }
                        h2 { font-size: 18px; margin-bottom: 15px; color: #374151; }
                        h3 { font-size: 16px; margin-bottom: 10px; color: #4b5563; }
                        table { border-collapse: collapse; width: 100%; margin: 15px 0; }
                        th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; }
                        th { background-color: #f3f4f6; font-weight: bold; }
                        .space-y-2 > * + * { margin-top: 8px; }
                        .space-y-3 > * + * { margin-top: 12px; }
                        .space-y-6 > * + * { margin-top: 24px; }
                        .grid { display: grid; gap: 20px; }
                        .md\\:grid-cols-2 { grid-template-columns: 1fr 1fr; }
                        .bg-white { background-color: white; }
                        .border { border: 1px solid #d1d5db; }
                        .rounded-lg { border-radius: 8px; }
                        .p-4 { padding: 16px; }
                        .p-6 { padding: 24px; }
                        .flex { display: flex; }
                        .justify-between { justify-content: space-between; }
                        .font-semibold { font-weight: 600; }
                        .text-blue-700 { color: #1d4ed8; }
                        .text-green-700 { color: #047857; }
                        .text-purple-700 { color: #7c3aed; }
                        .text-gray-600 { color: #4b5563; }
                        .text-gray-800 { color: #1f2937; }
                        @page { margin: 1in; size: letter; }
                        @media print {
                            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                            .print-page { page-break-after: always; }
                            .print-page:last-child { page-break-after: auto; }
                        }
                    </style>
                </head>
                <body>
                    ${allTabsContent}
                </body>
            </html>
        `);
        frameDoc.close();

        setTimeout(() => {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
            setTimeout(() => {
                document.body.removeChild(printFrame);
            }, 100);
        }, 500);
    };


    const runGoalSeek = useCallback(() => {
        setIsLoadingGoalSeek(true);
        setGoalSeekMessage('');
        setTimeout(() => {
            const targetIRR = inputs.goalSeekTargetIRR;
            const tolerance = 1e-5;
            const maxIterations = 100;
            const getIrrForFunds = (funds) => {
                if (funds <= 0) return -Infinity;
                return calculateFinancials({ ...inputs, ownFunds: funds }).indicators.projectIRR;
            };
            let lowFunds = 1;
            let highFunds = (inputs.installedCapacity * inputs.investmentPerMW) * 1.1;
            let irrAtLow = getIrrForFunds(lowFunds);
            let irrAtHigh = getIrrForFunds(highFunds);
            if (targetIRR < irrAtLow || targetIRR > irrAtHigh) {
                setGoalSeekMessage(`Target IRR (${formatPercentage(targetIRR)}) is unachievable. Must be between ${formatPercentage(irrAtLow)} and ${formatPercentage(irrAtHigh)}.`);
                setIsLoadingGoalSeek(false); return;
            }
            let midFunds = lowFunds;
            for (let i = 0; i < maxIterations; i++) {
                midFunds = lowFunds + (highFunds - lowFunds) / 2;
                if (midFunds === highFunds || midFunds === lowFunds) break;
                const irrAtMid = getIrrForFunds(midFunds);
                if (Math.abs(irrAtMid - targetIRR) < tolerance) break;
                if (irrAtMid < targetIRR) { lowFunds = midFunds; } else { highFunds = midFunds; }
            }
            setInputs(prev => ({ ...prev, ownFunds: midFunds }));
            setGoalSeekMessage(`Solution found for Own Funds: ${formatCurrency(midFunds)}`);
            setIsLoadingGoalSeek(false);
        }, 100);
    }, [inputs]);

    const tabs = ['Summary', 'Basic Data', 'Investment & Financing', 'Loan Repayment', 'Total Cost & Cost/kWh', 'Profit & Loss', 'Balance Sheet', 'EVA', 'Charts', 'README'];

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'Basic Data': return <BasicDataTab inputs={inputs} outputs={outputs} onChange={handleInputChange} onGoalSeek={runGoalSeek} isLoading={isLoadingGoalSeek} goalSeekMessage={goalSeekMessage} />;
            case 'Summary': return <SummaryTab outputs={outputs} inputs={inputs} />;
            case 'Investment & Financing': return <InvestmentFinancingTab outputs={outputs} />;
            case 'Loan Repayment': return <LoanRepaymentTab outputs={outputs} />;
            case 'Total Cost & Cost/kWh': return <TotalCostTab outputs={outputs} />;
            case 'Profit & Loss': return <ProfitLossTab outputs={outputs} />;
            case 'Balance Sheet': return <BalanceSheetTab outputs={outputs} />;
            case 'EVA': return <EVATab outputs={outputs} />;
            case 'Charts': return <ChartsTab outputs={outputs} isDarkMode={isDarkMode} />;
            case 'README': return <ReadmeTab outputs={outputs} inputs={inputs} isDarkMode={isDarkMode} />;
            default: return <SummaryTab outputs={outputs} inputs={inputs} />;
        }
    };

    return (
        <div className="container mx-auto p-4 sm:p-6 lg:p-8">
            <style>
                {`
                    @media print {
                        body * {
                            visibility: hidden;
                        }
                        .printable-area, .printable-area * {
                            visibility: visible;
                        }
                        .printable-area {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 100%;
                        }
                        .no-print {
                            display: none;
                        }
                    }
                `}
            </style>
            <header className="mb-6 flex justify-between items-center no-print">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Solar Project Financial Model</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">An interactive tool for project evaluation and forecasting.</p>
                </div>
                <div className="flex items-center gap-2">
                    <input type="file" ref={fileInputRef} onChange={handleImport} style={{ display: 'none' }} accept=".csv" />
                    <button onClick={handleImportClick} title="Import Parameters" className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                        <Upload size={20} />
                    </button>
                    <button onClick={handleExport} title="Export Parameters" className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                        <Download size={20} />
                    </button>
                     <button onClick={handlePrint} title="Print to PDF" className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                        <Printer size={20} />
                    </button>
                    <button onClick={() => setIsDarkMode(!isDarkMode)} title="Toggle Dark Mode" className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                        {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                    </button>
                </div>
            </header>
            <div className="no-print flex flex-wrap border-b border-gray-200 dark:border-gray-700">{tabs.map(tab => (<Tab key={tab} label={tab} isActive={activeTab === tab} onClick={() => setActiveTab(tab)} />))}</div>
            <main className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-b-lg shadow-lg printable-area">
                {renderActiveTab()}
            </main>
            <footer className="text-center mt-8 text-sm text-gray-500 dark:text-gray-400 no-print"><p>Ikutu - Sustainable Energy Solutions</p></footer>
        </div>
    );
}

// --- Tab Components ---

const SummaryTab = ({ outputs, inputs }) => (
    <div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Financial Indicators Summary</h2>
        <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-lg mb-3 text-blue-700 dark:text-blue-400">Project Investment Analysis</h3>
                <OutputCell label="Total Investment" value={outputs.investmentPlan?.totalInvestment} />
                <OutputCell label="Project NPV" value={outputs.indicators?.projectNPV} />
                <OutputCell label="Project IRR (after tax)" value={outputs.indicators?.projectIRR} formatter={formatPercentage} />
                <OutputCell label="Payback Period" value={outputs.indicators?.paybackPeriod} formatter={formatYears} />
                <OutputCell label="Discounted Payback Period" value={outputs.indicators?.discountedPaybackPeriod} formatter={formatYears} />
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-lg mb-3 text-green-700 dark:text-green-400">Capital Analysis</h3>
                <OutputCell label="Capital NPV" value={outputs.indicators?.capitalNPV} />
                <OutputCell label="Capital IRR (after tax)" value={outputs.indicators?.capitalIRR} formatter={formatPercentage} />
            </div>
             <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 md:col-span-2">
                <h3 className="font-semibold text-lg mb-3 text-purple-700 dark:text-purple-400">Key Metrics</h3>
                 <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                    <OutputCell label="LCOE" value={outputs.indicators?.lcoe} unit="USD/kWh" formatter={(v) => formatCurrency(v, 4)} />
                    <OutputCell label="Avg. Cost per kWh" value={outputs.costStatement?.reduce((a,b) => a + b.costPerKwh, 0) / outputs.costStatement?.length} unit="USD/kWh" formatter={(v) => formatCurrency(v, 4)} />
                    <OutputCell label="Total Net Profit" value={outputs.pnlStatement?.reduce((a,b) => a + b.netProfit, 0)} />
                    <OutputCell label="Total EVA" value={outputs.evaStatement?.reduce((a,b) => a + b.eva, 0)} />
                 </div>
            </div>
        </div>
        
        <div className="mt-8">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">🎯 Key Performance Indicators (KPIs)</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Investment Viability Metrics</h3>
                    <div className="space-y-4">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-blue-800 dark:text-blue-200">Project IRR &gt; Discount Rate</h4>
                            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">Indicates project viability</p>
                            <div className="mt-2 text-lg font-bold text-blue-600 dark:text-blue-400">
                                {formatPercentage(outputs.indicators?.projectIRR)} vs {formatPercentage(inputs.discountRate)}
                            </div>
                            <div className={`text-sm mt-1 ${(outputs.indicators?.projectIRR || 0) > inputs.discountRate ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {(outputs.indicators?.projectIRR || 0) > inputs.discountRate ? '✅ VIABLE' : '❌ NOT VIABLE'}
                            </div>
                        </div>
                        
                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-green-800 dark:text-green-200">Positive Project NPV</h4>
                            <p className="text-sm text-green-700 dark:text-green-300 mt-1">Confirms value creation</p>
                            <div className="mt-2 text-lg font-bold text-green-600 dark:text-green-400">
                                {formatCurrency(outputs.indicators?.projectNPV)}
                            </div>
                            <div className={`text-sm mt-1 ${(outputs.indicators?.projectNPV || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {(outputs.indicators?.projectNPV || 0) > 0 ? '✅ POSITIVE' : '❌ NEGATIVE'}
                            </div>
                        </div>
                        
                        <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-orange-800 dark:text-orange-200">LCOE &lt; Market Price</h4>
                            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">Ensures competitive positioning</p>
                            <div className="mt-2 text-lg font-bold text-orange-600 dark:text-orange-400">
                                {formatCurrency(outputs.indicators?.lcoe, 4)}/kWh
                            </div>
                        </div>
                        
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-purple-800 dark:text-purple-200">Payback Period &lt; 10 years</h4>
                            <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">Acceptable risk profile</p>
                            <div className="mt-2 text-lg font-bold text-purple-600 dark:text-purple-400">
                                {formatYears(outputs.indicators?.paybackPeriod)}
                            </div>
                            <div className={`text-sm mt-1 ${(outputs.indicators?.paybackPeriod || 0) < 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {(outputs.indicators?.paybackPeriod || 0) < 10 ? '✅ ACCEPTABLE' : '❌ TOO LONG'}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💰 Investor Return Metrics</h3>
                    <div className="space-y-4">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-blue-800 dark:text-blue-200">Capital IRR &gt; Target Return</h4>
                            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">Adequate equity returns</p>
                            <div className="mt-2 text-lg font-bold text-blue-600 dark:text-blue-400">
                                {formatPercentage(outputs.indicators?.capitalIRR)}
                            </div>
                        </div>
                        
                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-green-800 dark:text-green-200">Positive Capital NPV</h4>
                            <p className="text-sm text-green-700 dark:text-green-300 mt-1">Equity value creation</p>
                            <div className="mt-2 text-lg font-bold text-green-600 dark:text-green-400">
                                {formatCurrency(outputs.indicators?.capitalNPV)}
                            </div>
                            <div className={`text-sm mt-1 ${(outputs.indicators?.capitalNPV || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {(outputs.indicators?.capitalNPV || 0) > 0 ? '✅ POSITIVE' : '❌ NEGATIVE'}
                            </div>
                        </div>
                        
                        <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-orange-800 dark:text-orange-200">Stable Cash Distributions</h4>
                            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">Predictable investor returns</p>
                            <div className="mt-2 text-lg font-bold text-orange-600 dark:text-orange-400">
                                {formatPercentage(inputs.profitDistributionRate)} of profit
                            </div>
                        </div>
                        
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-purple-800 dark:text-purple-200">Positive EVA</h4>
                            <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">Value creation above cost of capital</p>
                            <div className="mt-2 text-lg font-bold text-purple-600 dark:text-purple-400">
                                {formatCurrency(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0)}
                            </div>
                            <div className={`text-sm mt-1 ${(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0) > 0 ? '✅ CREATING VALUE' : '❌ DESTROYING VALUE'}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🏥 Financial Health Metrics</h3>
                    <div className="space-y-4">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-blue-800 dark:text-blue-200">Debt Service Coverage &gt; 1.2</h4>
                            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">Adequate cash flow for debt service</p>
                            <div className="mt-2 text-sm text-blue-600 dark:text-blue-400">
                                Calculated from annual cash flows
                            </div>
                        </div>
                        
                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-green-800 dark:text-green-200">Operating Margin &gt; 20%</h4>
                            <p className="text-sm text-green-700 dark:text-green-300 mt-1">Healthy operational efficiency</p>
                            <div className="mt-2 text-lg font-bold text-green-600 dark:text-green-400">
                                {formatPercentage(1 - inputs.operatingCostRate)}
                            </div>
                            <div className={`text-sm mt-1 ${(1 - inputs.operatingCostRate) > 0.2 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {(1 - inputs.operatingCostRate) > 0.2 ? '✅ HEALTHY' : '❌ LOW MARGIN'}
                            </div>
                        </div>
                        
                        <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-orange-800 dark:text-orange-200">Cost Escalation &lt; 3%</h4>
                            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">Controlled cost growth</p>
                            <div className="mt-2 text-lg font-bold text-orange-600 dark:text-orange-400">
                                {formatPercentage(inputs.operatingCostEscalationRate)}
                            </div>
                            <div className={`text-sm mt-1 ${inputs.operatingCostEscalationRate < 0.03 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {inputs.operatingCostEscalationRate < 0.03 ? '✅ CONTROLLED' : '❌ HIGH ESCALATION'}
                            </div>
                        </div>
                        
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-purple-800 dark:text-purple-200">Balance Sheet Balance = 0</h4>
                            <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">Accounting accuracy verification</p>
                            <div className="mt-2 text-sm text-purple-600 dark:text-purple-400">
                                Verified in Balance Sheet tab
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Explanation title="How This Works">
            <p>The Summary tab provides a high-level overview of the project's financial viability. It aggregates the most critical metrics from all other sheets into one place.</p>
            <p><strong>Project Investment Analysis:</strong> These metrics evaluate the project on its own merits, independent of how it's financed. The Internal Rate of Return (IRR) is the discount rate at which the Net Present Value (NPV) of all cash flows equals zero. A project is typically considered viable if its IRR is greater than the discount rate (WACC).</p>
            <p><strong>Capital Analysis:</strong> These metrics evaluate the return specifically for the equity investors. It considers the timing and amount of their cash contributions (own funds) and the dividends they receive.</p>
            <div>
              <h5 className="font-bold text-gray-700 dark:text-gray-300">Key Metrics Explained:</h5>
              <p><strong>LCOE (Levelized Cost of Energy):</strong> This is the average revenue per unit of electricity that would be required to recover all costs over the project's lifetime. It includes the initial investment and discounts all future costs and energy production to account for the time value of money. It's the best "apples-to-apples" metric for comparing different power generation technologies.</p>
              <p><strong>Average Cost per kWh:</strong> This is a simple accounting average of the total costs from the "Total Cost" tab divided by the total energy produced. Unlike LCOE, it does not include the initial investment and does not account for the time value of money, which is why it is typically lower.</p>
              <p><strong>The Bottom Line:</strong> LCOE represents the true lifetime break-even price for energy, making it a superior metric for investment decisions. The Average Cost per kWh is more of a simple operational efficiency metric for a given period.</p>
            </div>
            <div>
              <h5 className="font-bold text-gray-700 dark:text-gray-300">Land Cost Integration:</h5>
              <p><strong>Land Requirements:</strong> The model calculates land requirements based on industry standards of approximately 5 acres per MW of installed capacity. This reflects the space needed for solar panels, inverters, access roads, maintenance areas, and buffer zones around the installation.</p>
              <p><strong>Land Cost Structure:</strong> Land costs are treated as annual operating expenses (lease/rental payments) rather than upfront capital investments. This reflects the typical practice in large-scale solar development where land is leased rather than purchased. The default rate is $1,000 per acre per year, but this can be adjusted based on local market conditions.</p>
              <p><strong>Financial Impact:</strong> Land costs significantly impact project economics as they represent a substantial ongoing operating expense. For a 10 MW project requiring 50 acres, annual land costs of $50,000 accumulate to $1.25 million over the 25-year project life. This is integrated into:</p>
              <ul className="ml-4 list-disc text-gray-600 dark:text-gray-400">
                <li><strong>LCOE Calculation:</strong> Land costs are included as part of the annual operating expenses, increasing the true cost of energy</li>
                <li><strong>Cash Flow Analysis:</strong> Annual land payments reduce project cash flows, affecting NPV and IRR calculations</li>
                <li><strong>Sensitivity Analysis:</strong> The model includes land cost sensitivity tables showing how NPV varies with different land cost scenarios</li>
                <li><strong>Total Cost Structure:</strong> Land costs appear as a separate line item in the Total Cost table, providing transparency in cost allocation</li>
              </ul>
              <p><strong>Real-World Relevance:</strong> Including land costs makes the financial model more realistic and comprehensive. Land acquisition or leasing is often one of the largest project costs after equipment, and can significantly vary by geographic location, making it a critical factor in project site selection and feasibility assessment.</p>
            </div>
        </Explanation>
    </div>
);

const NpvSensitivityTable = ({ inputs }) => {
    const { sensitivityData, tariffRange, investmentRange } = useMemo(() => {
        const baseTariff = inputs.onGridTariff;
        const baseInvestment = inputs.investmentPerMW;

        const tariffRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseTariff * (1 + mod));
        const investmentRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseInvestment * (1 + mod));

        const sensitivityData = investmentRange.map(inv => {
            return tariffRange.map(tariff => {
                const tempInputs = { ...inputs, onGridTariff: tariff, investmentPerMW: inv };
                return calculateFinancials(tempInputs).indicators.projectNPV;
            });
        });

        return { sensitivityData, tariffRange, investmentRange };
    }, [inputs]);

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">NPV Sensitivity Analysis (Tariff vs. Investment Cost)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                            <th className="p-2 border border-gray-300 dark:border-gray-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Inv/MW ↓</span>
                                    <span className="font-normal text-xs">Tariff →</span>
                                </div>
                            </th>
                            {tariffRange.map((tariff, index) => (
                                <th key={index} className="p-2 border border-gray-300 dark:border-gray-600 font-medium">{formatCurrency(tariff, 3)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {investmentRange.map((inv, rowIndex) => (
                            <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <th className="p-2 border border-gray-300 dark:border-gray-600 font-medium bg-gray-100 dark:bg-gray-700">{formatCurrency(inv)}</th>
                                {sensitivityData[rowIndex].map((npv, colIndex) => {
                                    const isBaseCase = rowIndex === 2 && colIndex === 2;
                                    const isNegative = npv < 0;
                                    return (
                                        <td key={colIndex} className={`p-2 border border-gray-300 dark:border-gray-600 ${isBaseCase ? 'bg-blue-100 dark:bg-blue-900/50 font-bold' : ''} ${isNegative ? 'text-red-500' : ''}`}>
                                            {formatCurrency(npv)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const CapitalIrrSensitivityTable = ({ inputs }) => {
    const { sensitivityData, tariffRange, ownFundsRange } = useMemo(() => {
        const baseTariff = inputs.onGridTariff;
        const baseOwnFunds = inputs.ownFunds;

        const tariffRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseTariff * (1 + mod));
        const ownFundsRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseOwnFunds * (1 + mod));

        const sensitivityData = ownFundsRange.map(funds => {
            return tariffRange.map(tariff => {
                const tempInputs = { ...inputs, onGridTariff: tariff, ownFunds: funds };
                return calculateFinancials(tempInputs).indicators.capitalIRR;
            });
        });

        return { sensitivityData, tariffRange, ownFundsRange };
    }, [inputs]);

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">Capital IRR Sensitivity Analysis (Tariff vs. Own Funds)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                            <th className="p-2 border border-gray-300 dark:border-gray-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Own Funds ↓</span>
                                    <span className="font-normal text-xs">Tariff →</span>
                                </div>
                            </th>
                            {tariffRange.map((tariff, index) => (
                                <th key={index} className="p-2 border border-gray-300 dark:border-gray-600 font-medium">{formatCurrency(tariff, 3)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {ownFundsRange.map((funds, rowIndex) => (
                            <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <th className="p-2 border border-gray-300 dark:border-gray-600 font-medium bg-gray-100 dark:bg-gray-700">{formatCurrency(funds)}</th>
                                {sensitivityData[rowIndex].map((irr, colIndex) => {
                                    const isBaseCase = rowIndex === 2 && colIndex === 2;
                                    return (
                                        <td key={colIndex} className={`p-2 border border-gray-300 dark:border-gray-600 ${isBaseCase ? 'bg-blue-100 dark:bg-blue-900/50 font-bold' : ''}`}>
                                            {formatPercentage(irr)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const ProjectIrrSensitivityTable = ({ inputs }) => {
    const { sensitivityData, waccRange, ownFundsRange } = useMemo(() => {
        const baseWacc = inputs.discountRate;
        const baseOwnFunds = inputs.ownFunds;

        const waccRange = [-0.02, -0.01, 0, 0.01, 0.02].map(mod => baseWacc + mod);
        const ownFundsRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseOwnFunds * (1 + mod));

        const sensitivityData = ownFundsRange.map(funds => {
            return waccRange.map(wacc => {
                const tempInputs = { ...inputs, ownFunds: funds, discountRate: wacc };
                return calculateFinancials(tempInputs).indicators.projectIRR;
            });
        });

        return { sensitivityData, waccRange, ownFundsRange };
    }, [inputs]);

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">Project IRR Sensitivity Analysis (WACC vs. Own Funds)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                            <th className="p-2 border border-gray-300 dark:border-gray-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Own Funds ↓</span>
                                    <span className="font-normal text-xs">WACC →</span>
                                </div>
                            </th>
                            {waccRange.map((wacc, index) => (
                                <th key={index} className="p-2 border border-gray-300 dark:border-gray-600 font-medium">{formatPercentage(wacc)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {ownFundsRange.map((funds, rowIndex) => (
                            <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <th className="p-2 border border-gray-300 dark:border-gray-600 font-medium bg-gray-100 dark:bg-gray-700">{formatCurrency(funds)}</th>
                                {sensitivityData[rowIndex].map((irr, colIndex) => {
                                    const isBaseCase = rowIndex === 2 && colIndex === 2;
                                    return (
                                        <td key={colIndex} className={`p-2 border border-gray-300 dark:border-gray-600 ${isBaseCase ? 'bg-blue-100 dark:bg-blue-900/50 font-bold' : ''}`}>
                                            {formatPercentage(irr)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                    Note: Project IRR is independent of the WACC (Discount Rate). The IRR values only change vertically with "Own Funds" because the financing structure affects the project's cash flows (e.g., interest payments).
                </p>
            </div>
        </div>
    );
};

const LandCostSensitivityTable = ({ inputs }) => {
    const { sensitivityData, tariffRange, landCostRange } = useMemo(() => {
        const baseTariff = inputs.onGridTariff;
        const baseLandCost = inputs.landCostPerAcre;

        const tariffRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseTariff * (1 + mod));
        const landCostRange = [-0.5, -0.25, 0, 0.25, 0.5].map(mod => baseLandCost * (1 + mod));

        const sensitivityData = landCostRange.map(landCost => {
            return tariffRange.map(tariff => {
                const tempInputs = { ...inputs, onGridTariff: tariff, landCostPerAcre: landCost };
                return calculateFinancials(tempInputs).indicators.projectNPV;
            });
        });

        return { sensitivityData, tariffRange, landCostRange };
    }, [inputs]);

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">NPV Sensitivity Analysis (Tariff vs. Land Cost)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                            <th className="p-2 border border-gray-300 dark:border-gray-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Land Cost ↓</span>
                                    <span className="font-normal text-xs">Tariff →</span>
                                </div>
                            </th>
                            {tariffRange.map((tariff, index) => (
                                <th key={index} className="p-2 border border-gray-300 dark:border-gray-600 font-medium">{formatCurrency(tariff, 3)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {landCostRange.map((landCost, rowIndex) => (
                            <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <th className="p-2 border border-gray-300 dark:border-gray-600 font-medium bg-gray-100 dark:bg-gray-700">{formatCurrency(landCost)}</th>
                                {sensitivityData[rowIndex].map((npv, colIndex) => {
                                    const isBaseCase = rowIndex === 2 && colIndex === 2;
                                    const isNegative = npv < 0;
                                    return (
                                        <td key={colIndex} className={`p-2 border border-gray-300 dark:border-gray-600 ${isBaseCase ? 'bg-blue-100 dark:bg-blue-900/50 font-bold' : ''} ${isNegative ? 'text-red-500' : ''}`}>
                                            {formatCurrency(npv)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


const BasicDataTab = ({ inputs, outputs, onChange, onGoalSeek, isLoading, goalSeekMessage }) => (
    <div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Basic Data & Assumptions</h2>
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-4">
            <div>
                <SectionHeader title="Project Parameters" />
                <InputCell id="installedCapacity" label="Installed Capacity" value={inputs.installedCapacity} onChange={onChange} unit="MWp" />
                <InputCell id="investmentPerMW" label="Investment per MW" value={inputs.investmentPerMW} onChange={onChange} unit="USD/MWp" />
                <InputCell id="annualHours" label="Annual Utilization Hours" value={inputs.annualHours} onChange={onChange} unit="h" />
                <InputCell id="onGridTariff" label="On-grid Tariff (ex-tax)" value={inputs.onGridTariff} onChange={onChange} unit="USD/kWh" step="0.01" />
                <InputCell id="pvDegradationRate" label="PV Degradation / Year" value={inputs.pvDegradationRate} onChange={onChange} isPercentage unit="%" />
                <InputCell id="constructionPeriod" label="Construction Period" value={inputs.constructionPeriod} onChange={onChange} unit="year" />
                <InputCell id="operationPeriod" label="Operation Period" value={inputs.operationPeriod} onChange={onChange} unit="year" />
                <InputCell id="landCostPerAcre" label="Land Cost per Acre (annual)" value={inputs.landCostPerAcre} onChange={onChange} unit="USD/acre/year" />
                <InputCell id="acresPerMW" label="Acres per MW" value={inputs.acresPerMW} onChange={onChange} unit="acres/MW" />
                <OutputCell label="Total Acres Required" value={inputs.installedCapacity * inputs.acresPerMW} unit="acres" formatter={(v) => v.toFixed(1)} />
                <OutputCell label="Annual Land Cost" value={inputs.installedCapacity * inputs.acresPerMW * inputs.landCostPerAcre} formatter={formatCurrency} />
            </div>
            <div>
                <SectionHeader title="Financing & Capital" />
                <InputCell id="ownFunds" label="Own Funds" value={inputs.ownFunds} onChange={onChange} unit="USD" />
                <InputCell id="longTermLoanRate" label="Loan Interest Rate" value={inputs.longTermLoanRate} onChange={onChange} isPercentage unit="%" />
                <OutputCell label="Capital Fund Ratio" value={outputs.investmentPlan?.capitalFundRatio} formatter={formatPercentage} />
                <OutputCell label="Total Investment" value={outputs.investmentPlan?.totalInvestment} unit="USD" />
                <InputCell id="discountRate" label="Discount Rate (WACC)" value={inputs.discountRate} onChange={onChange} isPercentage unit="%" />
            </div>
            <div>
                <SectionHeader title="Costs & Depreciation" />
                <InputCell id="operatingCostRate" label="Operating Cost Rate" value={inputs.operatingCostRate} onChange={onChange} isPercentage unit="% of revenue" />
                <InputCell id="operatingCostEscalationRate" label="Op. Cost Escalation Rate" value={inputs.operatingCostEscalationRate} onChange={onChange} isPercentage unit="%" />
                <InputCell id="depreciationPeriod" label="Depreciation Period" value={inputs.depreciationPeriod} onChange={onChange} unit="year" />
                <InputCell id="salvageValueRate" label="Salvage Value Rate" value={inputs.salvageValueRate} onChange={onChange} isPercentage unit="%" />
            </div>
            <div>
                <SectionHeader title="Tax & Profit Distribution" />
                <InputCell id="vatRate" label="VAT Rate" value={inputs.vatRate} onChange={onChange} isPercentage unit="%" />
                <InputCell id="surtaxRate" label="Surtax Rate (of VAT)" value={inputs.surtaxRate} onChange={onChange} isPercentage unit="%" />
                <InputCell id="incomeTaxRate" label="Income Tax Rate" value={inputs.incomeTaxRate} onChange={onChange} isPercentage unit="%" />
                <InputCell id="statutoryReserveRate" label="Statutory Reserve" value={inputs.statutoryReserveRate} onChange={onChange} isPercentage unit="%" />
                <InputCell id="discretionaryReserveRate" label="Discretionary Reserve" value={inputs.discretionaryReserveRate} onChange={onChange} isPercentage unit="%" />
                <InputCell id="profitDistributionRate" label="Profit Distribution" value={inputs.profitDistributionRate} onChange={onChange} isPercentage unit="%" />
            </div>
            <div className="md:col-span-2">
                <SectionHeader title="Valuation & Goal Seeking" />
                <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-blue-50 dark:bg-gray-800/50">
                     <div className="grid md:grid-cols-3 gap-4 items-center">
                        <InputCell id="goalSeekTargetIRR" label="Target Project IRR" value={inputs.goalSeekTargetIRR} onChange={onChange} isPercentage unit="%" />
                        <OutputCell label="Current Project IRR" value={outputs.indicators?.projectIRR} formatter={formatPercentage} />
                        <button onClick={onGoalSeek} disabled={isLoading} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 h-10">
                            {isLoading ? 'Calculating...' : 'Find Required Own Funds'}
                        </button>
                    </div>
                    {goalSeekMessage && <p className={`text-sm mt-2 font-medium ${goalSeekMessage.startsWith('Target') ? 'text-red-600' : 'text-green-600'}`}>{goalSeekMessage}</p>}
                </div>
                <NpvSensitivityTable inputs={inputs} />
                <CapitalIrrSensitivityTable inputs={inputs} />
                <ProjectIrrSensitivityTable inputs={inputs} />
                <LandCostSensitivityTable inputs={inputs} />
            </div>
        </div>
        <Explanation title="How This Works">
            <p>This tab is the control panel for the entire financial model. All cells with a white background are inputs that you can change to see their impact on the project's financials.</p>
            <p><strong>Land Cost Integration:</strong> The model now includes land costs as an annual operating expense. Based on your input for "Land Cost per Acre" and "Acres per MW", the model calculates the total land area needed and the annual land lease cost. This affects all financial metrics including LCOE, NPV, and IRR. The default assumption is 5 acres per MW, which is typical for utility-scale solar projects.</p>
            <p><strong>Principle:</strong> The core principle here is sensitivity analysis. By changing these base assumptions (like the cost of investment, the price of electricity, interest rates, or land costs), you can model different scenarios (e.g., best-case, worst-case, expected-case) to understand the project's risks and opportunities.</p>
            <p><strong>Goal:</strong> The goal is to provide a single place to define all the variables that drive the model. Any change here will automatically flow through to all other tabs, recalculating every financial statement in real-time.</p>
        </Explanation>
    </div>
);

const Table = ({ headers, keys, data, formatters, boldRows = [] }) => (<div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700"><thead className="bg-gray-50 dark:bg-gray-700/50"><tr>{headers.map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{h}</th>)}</tr></thead><tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">{data.map((row, rowIndex) => (<tr key={rowIndex} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${boldRows.includes(rowIndex) ? 'font-bold' : ''}`}>{keys.map(key => (<td key={key} className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{formatters[key] ? formatters[key](row[key]) : row[key]}</td>))}</tr>))}</tbody></table></div>);

const InvestmentFinancingTab = ({ outputs }) => {
    const planData = outputs.investmentPlan ? [{ item: 'Total Static Investment', value: outputs.investmentPlan.totalStaticInvestment}, { item: 'Interest During Construction', value: outputs.investmentPlan.interestDuringConstruction }, { item: 'Working Capital', value: outputs.investmentPlan.workingCapital }, { item: 'Total Investment', value: outputs.investmentPlan.totalInvestment}] : [];
    const financingData = outputs.financing ? [{ item: 'Capital (Own Funds)', value: outputs.financing.capital }, { item: 'Loan', value: outputs.financing.loan }, { item: 'Total Financing', value: outputs.financing.totalFinancing}] : [];
    return (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Investment Plan & Financing</h2><div className="grid md:grid-cols-2 gap-8"><div><h3 className="text-lg font-semibold mb-2">Investment Plan</h3><div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">{planData.map(d => <OutputCell key={d.item} label={d.item} value={d.value} />)}</div></div><div><h3 className="text-lg font-semibold mb-2">Financing Sources</h3><div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">{financingData.map(d => <OutputCell key={d.item} label={d.item} value={d.value} />)}</div></div></div>
        <Explanation title="How This Works">
            <p>This tab breaks down the total cost of the project (Uses of Funds) and how it will be paid for (Sources of Funds).</p>
            <p><strong>Principle:</strong> The fundamental accounting equation is at play here: Assets = Liabilities + Equity. In this context, the Total Investment (the assets being created) must equal the Total Financing (the liabilities/loan + equity/own funds used to pay for them).</p>
            <p><strong>Goal:</strong> To clearly show the total capital required for the project and the mix of debt and equity that will be used. This is critical for understanding the project's capital structure and leverage.</p>
        </Explanation>
    </div>);
};

const LoanRepaymentTab = ({ outputs }) => (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Loan Repayment Schedule</h2><Table headers={['Year', 'Principal', 'Interest', 'Total Payment', 'Remaining Loan']} keys={['year', 'principal', 'interest', 'totalPayment', 'remainingLoan']} data={outputs.loanRepaymentSchedule || []} formatters={{'principal': formatCurrency, 'interest': formatCurrency, 'totalPayment': formatCurrency, 'remainingLoan': formatCurrency}} />
    <Explanation title="How This Works">
        <p>This tab details the year-by-year repayment of the loan taken out to finance the project.</p>
        <p><strong>Principle:</strong> This is a standard loan amortization schedule. Each year, a payment is made that consists of two parts: the principal (which reduces the loan balance) and the interest (the cost of borrowing). This model uses an equal principal repayment method, where the same amount of principal is paid back each year.</p>
        <p><strong>Goal:</strong> To calculate the annual interest and principal payments. The interest expense flows into the Profit & Loss statement, affecting profitability. Both principal and interest payments are cash outflows that impact the Balance Sheet and Cash Flow calculations.</p>
    </Explanation>
</div>);

const TotalCostTab = ({ outputs }) => (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Total Cost & Cost per kWh</h2><Table headers={['Year', 'Operating Cost', 'Land Cost', 'Depreciation', 'Interest', 'Total Cost', 'Cost per kWh']} keys={['year', 'operatingCost', 'landCost', 'depreciation', 'interest', 'totalCost', 'costPerKwh']} data={outputs.costStatement || []} formatters={{'operatingCost': formatCurrency, 'landCost': formatCurrency, 'depreciation': formatCurrency, 'interest': formatCurrency, 'totalCost': formatCurrency, 'costPerKwh': (v) => formatCurrency(v, 4)}} />
    <Explanation title="How This Works">
        <p>This statement breaks down all the costs associated with running the solar plant each year.</p>
        <p><strong>Principle:</strong> It separates costs into different categories. <strong>Operating Costs</strong> are the day-to-day expenses. <strong>Land Cost</strong> is the annual lease/rent for the land required by the solar farm. <strong>Depreciation</strong> is a non-cash expense that represents the "using up" of the physical assets over time. <strong>Interest</strong> is the cost of the project's debt. The "Cost per kWh" is a key performance indicator showing the all-in cost to produce one unit of electricity.</p>
        <p><strong>Goal:</strong> To provide a detailed view of the project's cost structure. These total costs are subtracted from revenue in the Profit & Loss statement to determine the project's profitability.</p>
    </Explanation>
</div>);

const ProfitLossTab = ({ outputs }) => (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Profit and Loss Statement</h2><Table headers={['Year', 'Operating Income', 'VAT & Surtax', 'Total Profit', 'Income Tax', 'Net Profit', 'Profit Distribution', 'Retained Earnings']} keys={['year', 'operatingIncome', 'vatSurtax', 'totalProfit', 'incomeTax', 'netProfit', 'profitDistribution', 'retainedEarnings']} data={outputs.pnlStatement || []} formatters={{'operatingIncome': formatCurrency, 'vatSurtax': formatCurrency, 'totalProfit': formatCurrency, 'incomeTax': formatCurrency, 'netProfit': formatCurrency, 'profitDistribution': formatCurrency, 'retainedEarnings': formatCurrency}} />
    <Explanation title="How This Works">
        <p>The Profit & Loss (P&L), or Income Statement, shows the project's financial performance over a period of time.</p>
        <p><strong>Principle:</strong> It follows the basic formula: Revenue - Expenses = Profit. It starts with Operating Income (revenue from selling electricity), subtracts all costs (from the Total Cost tab) and taxes, to arrive at the Net Profit (the "bottom line").</p>
        <p><strong>Goal:</strong> To determine if the project is profitable. The P&L also shows how much profit is available to be distributed to investors as dividends and how much is retained in the business to support future growth.</p>
    </Explanation>
</div>);

const BalanceSheetTab = ({ outputs }) => (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Balance Sheet</h2><Table headers={['Year', 'Total Assets', 'Total Liabilities', 'Total Equity', 'Total Liab. & Equity', 'Check']} keys={['year', 'totalAssets', 'totalLiabilities', 'totalEquity', 'totalLiabilitiesAndEquity', 'check']} data={outputs.balanceSheet || []} formatters={{'totalAssets': formatCurrency, 'totalLiabilities': formatCurrency, 'totalEquity': formatCurrency, 'totalLiabilitiesAndEquity': formatCurrency, 'check': (v) => formatCurrency(v, 0)}} />
    <Explanation title="How This Works">
        <p>The Balance Sheet provides a snapshot of the project's financial position at a single point in time (the end of each year).</p>
        <p><strong>Principle:</strong> It is built on the fundamental accounting equation: <strong>Assets = Liabilities + Equity</strong>. Assets are what the company owns (the solar plant, cash). Liabilities are what it owes (loans). Equity is the value belonging to the owners. The "Check" column should always be zero, ensuring the model is balanced.</p>
        <p><strong>Goal:</strong> To show the company's net worth and how its assets are financed. It provides a clear picture of the project's financial health and structure year over year.</p>
    </Explanation>
</div>);

const EVATab = ({ outputs }) => (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Economic Value Added (EVA)</h2><Table headers={['Year', 'EVA']} keys={['year', 'eva']} data={outputs.evaStatement || []} formatters={{ 'eva': formatCurrency }} />
    <Explanation title="How This Works">
        <p>Economic Value Added (EVA) is a measure of a company's financial performance based on the residual wealth calculated by deducting its cost of capital from its operating profit.</p>
        <p><strong>Principle:</strong> The formula is EVA = NOPAT - (Capital Employed * WACC), where NOPAT is Net Operating Profit After Tax and WACC is the Weighted Average Cost of Capital (represented by our Discount Rate). A positive EVA means the project is generating returns in excess of its cost of capital, thus creating value. A negative EVA means it is destroying value.</p>
        <p><strong>Goal:</strong> To provide a performance metric that is more comprehensive than simple accounting profit, as it explicitly accounts for the cost of the capital used to generate that profit.</p>
    </Explanation>
</div>);

const ReadmeTab = ({ outputs, inputs, isDarkMode }) => {
    const [activeSection, setActiveSection] = useState('overview');
    
    // Sample data for concept visualization
    const conceptData = [
        { year: 0, projectCF: -outputs.investmentPlan?.totalInvestment || -5000000, capitalCF: -inputs.ownFunds },
        { year: 1, projectCF: 800000, capitalCF: 400000 },
        { year: 5, projectCF: 750000, capitalCF: 380000 },
        { year: 10, projectCF: 700000, capitalCF: 350000 },
        { year: 15, projectCF: 650000, capitalCF: 320000 },
        { year: 20, projectCF: 600000, capitalCF: 300000 },
        { year: 25, projectCF: 550000, capitalCF: 280000 }
    ];

    const cashFlowChart = (
        <ResponsiveContainer width="100%" height={300}>
            <LineChart data={conceptData}>
                <CartesianGrid strokeDasharray="3 3" className={isDarkMode ? 'opacity-30' : 'opacity-60'} />
                <XAxis dataKey="year" className={isDarkMode ? 'fill-gray-300' : 'fill-gray-600'} />
                <YAxis className={isDarkMode ? 'fill-gray-300' : 'fill-gray-600'} />
                <Tooltip 
                    contentStyle={{
                        backgroundColor: isDarkMode ? '#374151' : '#ffffff',
                        border: isDarkMode ? '1px solid #6B7280' : '1px solid #D1D5DB',
                        color: isDarkMode ? '#F3F4F6' : '#111827'
                    }}
                    formatter={(value) => [formatCurrency(value), '']}
                />
                <Legend />
                <Line type="monotone" dataKey="projectCF" stroke="#3B82F6" strokeWidth={3} name="Project Cash Flow" />
                <Line type="monotone" dataKey="capitalCF" stroke="#10B981" strokeWidth={3} name="Capital Cash Flow" />
            </LineChart>
        </ResponsiveContainer>
    );

    const menuItems = [
        { id: 'overview', label: '🌟 Overview', icon: '📊' },
        { id: 'architecture', label: '🏗️ Model Architecture', icon: '🔧' },
        { id: 'inputs', label: '🎯 Input Parameters', icon: '⚙️' },
        { id: 'calculations', label: '🧮 Financial Formulas', icon: '📐' },
        { id: 'tab-analysis', label: '📈 Tab-by-Tab Analysis', icon: '📋' },
        { id: 'relationships', label: '🔗 Input-Output Matrix', icon: '🔄' },
        { id: 'assumptions', label: '🏗️ Assumptions & Limitations', icon: '⚠️' },
        { id: 'kpis', label: '🎯 Key Performance Indicators', icon: '📊' },
        { id: 'sensitivity', label: '🔄 Sensitivity Analysis', icon: '📉' },
        { id: 'implementation', label: '🛠️ Technical Implementation', icon: '⚙️' },
        { id: 'usage', label: '📚 Usage Guidelines', icon: '👥' },
        { id: 'extensions', label: '🔮 Model Extensions', icon: '🚀' },
        { id: 'theory', label: '🔬 Financial Theory', icon: '🧪' },
        { id: 'about', label: '👨‍💼 About', icon: 'ℹ️' }
    ];

    const renderSection = () => {
        switch(activeSection) {
            case 'overview':
                return (
                    <div className="space-y-8">
                        <div className="bg-gradient-to-r from-blue-500 to-green-500 text-white p-8 rounded-xl">
                            <h2 className="text-3xl font-bold mb-4">🌟 Solar Financial Model</h2>
                            <p className="text-xl mb-4">Complete Reference Guide & Documentation</p>
                            <p className="text-lg opacity-90">
                                This comprehensive solar financial model provides detailed financial analysis for utility-scale 
                                solar photovoltaic (PV) projects. The model evaluates project feasibility from both project-level 
                                and investor-level perspectives, incorporating industry-standard financial metrics and assumptions.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 Model Features</h3>
                                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                                    <li>✅ 9 Interactive Analysis Tabs</li>
                                    <li>✅ Real-time Financial Calculations</li>
                                    <li>✅ Goal Seek Optimization</li>
                                    <li>✅ Export/Import Parameters</li>
                                    <li>✅ Dark Mode Interface</li>
                                    <li>✅ Comprehensive Documentation</li>
                                </ul>
                            </div>
                            
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🎯 Key Metrics</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">Project IRR:</span>
                                        <span className="font-semibold text-blue-600 dark:text-blue-400">
                                            {formatPercentage(outputs.indicators?.projectIRR)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">Project NPV:</span>
                                        <span className="font-semibold text-green-600 dark:text-green-400">
                                            {formatCurrency(outputs.indicators?.projectNPV)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">LCOE:</span>
                                        <span className="font-semibold text-orange-600 dark:text-orange-400">
                                            {formatCurrency(outputs.indicators?.lcoe, 4)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Cash Flow Visualization</h3>
                            <p className="text-gray-600 dark:text-gray-300 mb-4">
                                Understanding the difference between Project Cash Flow (unlevered) and Capital Cash Flow (equity returns):
                            </p>
                            {cashFlowChart}
                        </div>
                    </div>
                );
                
            case 'architecture':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🏗️ Model Architecture</h2>
                        
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 9 Interactive Tabs</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {[
                                    { name: 'Basic Data', desc: 'Input parameters and project configuration', icon: '⚙️' },
                                    { name: 'Summary', desc: 'High-level financial metrics and KPIs', icon: '📋' },
                                    { name: 'Investment & Financing', desc: 'Capital requirements and funding structure', icon: '💰' },
                                    { name: 'Loan Repayment', desc: 'Debt service schedule and cash flows', icon: '📅' },
                                    { name: 'Total Cost & Cost/kWh', desc: 'Operating cost analysis and energy costs', icon: '💵' },
                                    { name: 'Profit & Loss', desc: 'Annual income statements', icon: '📈' },
                                    { name: 'Balance Sheet', desc: 'Financial position statements', icon: '⚖️' },
                                    { name: 'EVA', desc: 'Economic Value Added analysis', icon: '💎' },
                                    { name: 'Charts', desc: 'Visual representations of key metrics', icon: '📊' }
                                ].map((tab, index) => (
                                    <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <span className="text-2xl">{tab.icon}</span>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">{tab.name}</h4>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">{tab.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🔄 Calculation Flow</h3>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                    <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">1</div>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 dark:text-gray-100">Input Parameters</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">User defines project configuration, costs, and financial assumptions</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">2</div>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 dark:text-gray-100">Investment Calculation</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">Determines total investment, financing structure, and depreciation base</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                                    <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold">3</div>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 dark:text-gray-100">Annual Operations</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">Calculates yearly revenue, costs, taxes, and cash flows for each year</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                                    <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold">4</div>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 dark:text-gray-100">Financial Analysis</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">Computes NPV, IRR, LCOE, payback periods, and other key metrics</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'inputs':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🎯 Input Parameters & Assumptions</h2>
                        
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📋 Complete Parameter Reference</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                    <thead>
                                        <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                                            <th className="text-left py-3 px-4 font-bold text-gray-800 dark:text-gray-100">Parameter</th>
                                            <th className="text-left py-3 px-4 font-bold text-gray-800 dark:text-gray-100">Current Value</th>
                                            <th className="text-left py-3 px-4 font-bold text-gray-800 dark:text-gray-100">Unit</th>
                                            <th className="text-left py-3 px-4 font-bold text-gray-800 dark:text-gray-100">Description</th>
                                            <th className="text-left py-3 px-4 font-bold text-gray-800 dark:text-gray-100">Financial Impact</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                        <tr className="bg-blue-50 dark:bg-blue-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-blue-800 dark:text-blue-200">Project Configuration</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">constructionPeriod</td>
                                            <td className="py-3 px-4 font-semibold">{inputs.constructionPeriod}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">years</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Time required for project construction</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Affects interest during construction</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">operationPeriod</td>
                                            <td className="py-3 px-4 font-semibold">{inputs.operationPeriod}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">years</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Project operational lifetime</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Determines all cash flow periods</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">installedCapacity</td>
                                            <td className="py-3 px-4 font-semibold">{inputs.installedCapacity}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">MW</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Total solar installation capacity</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Primary driver of investment costs and revenue</td>
                                        </tr>
                                        
                                        <tr className="bg-green-50 dark:bg-green-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-green-800 dark:text-green-200">Investment Parameters</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">investmentPerMW</td>
                                            <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.investmentPerMW)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD/MW</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Capital cost per megawatt installed</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Direct multiplier for total investment</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">ownFunds</td>
                                            <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.ownFunds)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Equity contribution from investors</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Determines debt/equity ratio</td>
                                        </tr>
                                        
                                        <tr className="bg-orange-50 dark:bg-orange-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-orange-800 dark:text-orange-200">Revenue Parameters</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">annualHours</td>
                                            <td className="py-3 px-4 font-semibold">{inputs.annualHours}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">hours</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Equivalent full-load hours per year</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Primary revenue driver</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">onGridTariff</td>
                                            <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.onGridTariff, 4)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD/kWh</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Electricity selling price</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Revenue calculation base</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">pvDegradationRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.pvDegradationRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%/year</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Annual efficiency decline of PV panels</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Reduces energy output over time</td>
                                        </tr>
                                        
                                        <tr className="bg-red-50 dark:bg-red-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-red-800 dark:text-red-200">Cost Parameters</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">operatingCostRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.operatingCostRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">% of revenue</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">O&M costs as percentage of revenue</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Annual operating expense</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">operatingCostEscalationRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.operatingCostEscalationRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%/year</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Annual increase in operating costs</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Cost inflation over project life</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">landCostPerAcre</td>
                                            <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.landCostPerAcre)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD/acre/year</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Annual land lease cost</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Ongoing operating expense</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">acresPerMW</td>
                                            <td className="py-3 px-4 font-semibold">{inputs.acresPerMW}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">acres/MW</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Land requirement per MW capacity</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Determines total land costs</td>
                                        </tr>
                                        
                                        <tr className="bg-purple-50 dark:bg-purple-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-purple-800 dark:text-purple-200">Financing Parameters</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">longTermLoanRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.longTermLoanRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%/year</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Interest rate on project debt</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Debt service calculations</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">discountRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.discountRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%/year</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Weighted Average Cost of Capital (WACC)</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">NPV and LCOE calculations</td>
                                        </tr>
                                        
                                        <tr className="bg-yellow-50 dark:bg-yellow-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-yellow-800 dark:text-yellow-200">Tax & Accounting Parameters</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">vatRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.vatRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Value Added Tax rate</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Revenue taxation</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">surtaxRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.surtaxRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Additional tax on VAT</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Revenue taxation</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">incomeTaxRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.incomeTaxRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Corporate income tax rate</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Profit taxation</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">depreciationPeriod</td>
                                            <td className="py-3 px-4 font-semibold">{inputs.depreciationPeriod}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">years</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Asset depreciation timeline</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Tax shield and cash flow</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">salvageValueRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.salvageValueRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Residual asset value at end of life</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Terminal value calculation</td>
                                        </tr>
                                        
                                        <tr className="bg-teal-50 dark:bg-teal-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-teal-800 dark:text-teal-200">Profit Distribution Parameters</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">statutoryReserveRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.statutoryReserveRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">% of profit</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Legally required profit reserves</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Reduces distributable profit</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">discretionaryReserveRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.discretionaryReserveRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">% of profit</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Management discretionary reserves</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Reduces distributable profit</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">profitDistributionRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.profitDistributionRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Percentage of available profit distributed</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Investor cash returns</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );

            case 'calculations':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🧮 Financial Calculation Engine</h2>
                        
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">1. Investment & Financing Calculations</h3>
                            <div className="space-y-6">
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-3">Total Investment Estimation</h4>
                                    <div className="font-mono text-sm space-y-2">
                                        <div>Total Investment Estimate = Installed Capacity × Investment per MW</div>
                                        <div>= {inputs.installedCapacity} MW × {formatCurrency(inputs.investmentPerMW)} = <span className="font-bold text-green-600">{formatCurrency(inputs.installedCapacity * inputs.investmentPerMW)}</span></div>
                                    </div>
                                </div>
                                
                                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-3">Investment Component Breakdown</h4>
                                    <div className="font-mono text-sm space-y-1">
                                        <div>Equipment Purchase = Total Investment × 70% = {formatCurrency((inputs.installedCapacity * inputs.investmentPerMW) * 0.7)}</div>
                                        <div>Installation Project = Total Investment × 15% = {formatCurrency((inputs.installedCapacity * inputs.investmentPerMW) * 0.15)}</div>
                                        <div>Other Construction Costs = Total Investment × 10% = {formatCurrency((inputs.installedCapacity * inputs.investmentPerMW) * 0.1)}</div>
                                        <div>Preparatory Work = Total Investment × 5% = {formatCurrency((inputs.installedCapacity * inputs.investmentPerMW) * 0.05)}</div>
                                        <div className="font-bold">Total Static Investment = Sum of all components above</div>
                                    </div>
                                </div>
                                
                                <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-orange-800 dark:text-orange-200 mb-3">Construction Financing</h4>
                                    <div className="font-mono text-sm space-y-1">
                                        <div>Capital Fund Ratio = Own Funds ÷ Total Investment Estimate</div>
                                        <div>Loan Ratio = 1 - Capital Fund Ratio</div>
                                        <div>Construction Loan = Total Investment × Loan Ratio</div>
                                        <div>Interest During Construction = Construction Loan × Long Term Loan Rate × Construction Period</div>
                                    </div>
                                </div>
                                
                                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 dark:text-purple-200 mb-3">Working Capital & Final Investment</h4>
                                    <div className="font-mono text-sm space-y-1">
                                        <div>Working Capital = Total Investment × 2%</div>
                                        <div>Total Investment = Total Static Investment + Interest During Construction + Working Capital</div>
                                        <div className="font-bold">Final Financing Structure:</div>
                                        <div>Capital (Equity) = Own Funds = {formatCurrency(inputs.ownFunds)}</div>
                                        <div>Loan (Debt) = Total Investment - Capital</div>
                                        <div>Total Financing = Capital + Loan</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">2. Annual Operations Calculations</h3>
                            <div className="space-y-6">
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-3">Energy Generation with Degradation</h4>
                                    <div className="font-mono text-sm space-y-1">
                                        <div>Degradation Factor(year) = (1 - PV Degradation Rate)^(year-1)</div>
                                        <div>Annual Generation(year) = Installed Capacity × 1000 × Annual Hours × Degradation Factor</div>
                                        <div className="mt-2 space-y-1">
                                            <div>Year 1: {inputs.installedCapacity} × 1000 × {inputs.annualHours} = {formatCurrency(inputs.installedCapacity * 1000 * inputs.annualHours, 0)} kWh</div>
                                            <div>Year 10: {formatCurrency(inputs.installedCapacity * 1000 * inputs.annualHours * Math.pow(1 - inputs.pvDegradationRate, 9), 0)} kWh</div>
                                            <div>Year 25: {formatCurrency(inputs.installedCapacity * 1000 * inputs.annualHours * Math.pow(1 - inputs.pvDegradationRate, 24), 0)} kWh</div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-3">Revenue & Cost Structure</h4>
                                    <div className="font-mono text-sm space-y-1">
                                        <div>Operating Income(year) = Annual Generation(year) × On Grid Tariff</div>
                                        <div>Operating Cost(year=1) = Operating Income × Operating Cost Rate</div>
                                        <div>Operating Cost(year&gt;1) = Previous Year Cost × (1 + Escalation Rate)</div>
                                        <div>Land Cost(year) = Installed Capacity × Acres per MW × Land Cost per Acre</div>
                                        <div>= {inputs.installedCapacity} × {inputs.acresPerMW} × {formatCurrency(inputs.landCostPerAcre)} = {formatCurrency(inputs.installedCapacity * inputs.acresPerMW * inputs.landCostPerAcre)}/year</div>
                                    </div>
                                </div>
                                
                                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-red-800 dark:text-red-200 mb-3">Taxation & Profit Distribution</h4>
                                    <div className="font-mono text-sm space-y-1">
                                        <div>VAT Surtax = Operating Income × VAT Rate × Surtax Rate</div>
                                        <div>Total Profit = Operating Income - Total Cost - VAT Surtax</div>
                                        <div>Income Tax = MAX(0, Total Profit × Income Tax Rate)</div>
                                        <div>Net Profit = Total Profit - Income Tax</div>
                                        <div>Statutory Reserve = MAX(0, Net Profit × Statutory Reserve Rate)</div>
                                        <div>Discretionary Reserve = MAX(0, Net Profit × Discretionary Reserve Rate)</div>
                                        <div>Profit Distribution = Available Profit × Distribution Rate</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">3. Financial Indicators</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Net Present Value (NPV)</h4>
                                    <div className="font-mono text-xs space-y-1">
                                        <div>Project NPV = Σ[Project Cash Flow(year) ÷ (1 + Discount Rate)^year]</div>
                                        <div>Capital NPV = Σ[Capital Cash Flow(year) ÷ (1 + Discount Rate)^year]</div>
                                        <div className="mt-2 font-bold">Current Project NPV: {formatCurrency(outputs.indicators?.projectNPV)}</div>
                                    </div>
                                </div>
                                
                                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">Internal Rate of Return (IRR)</h4>
                                    <div className="font-mono text-xs space-y-1">
                                        <div>NPV(IRR) = 0 = Σ[Cash Flow(year) ÷ (1 + IRR)^year]</div>
                                        <div>Solved iteratively using Newton-Raphson method</div>
                                        <div className="mt-2 font-bold">Current Project IRR: {formatPercentage(outputs.indicators?.projectIRR)}</div>
                                    </div>
                                </div>
                                
                                <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-orange-800 dark:text-orange-200 mb-2">LCOE Calculation</h4>
                                    <div className="font-mono text-xs space-y-1">
                                        <div>Total Discounted Costs = Investment + Discounted O&M - Salvage Value</div>
                                        <div>LCOE = Total Discounted Costs ÷ Discounted Energy Production</div>
                                        <div className="mt-2 font-bold">Current LCOE: {formatCurrency(outputs.indicators?.lcoe, 4)}/kWh</div>
                                    </div>
                                </div>
                                
                                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 dark:text-purple-200 mb-2">Economic Value Added (EVA)</h4>
                                    <div className="font-mono text-xs space-y-1">
                                        <div>NOPAT = Net Profit + Interest × (1 - Tax Rate)</div>
                                        <div>Capital Employed = Total Assets - Current Liabilities</div>
                                        <div>EVA = NOPAT - Capital Employed × Discount Rate</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'tab-analysis':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">📈 Tab-by-Tab Analysis</h2>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📋 Tab 1: Basic Data</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Input configuration and parameter adjustment</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Key Features:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Editable input parameters</li>
                                        <li>Goal seek functionality for target IRR</li>
                                        <li>Parameter export/import capabilities</li>
                                        <li>Real-time calculation updates</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Goal Seek Function:</strong> Automatically adjusts the onGridTariff parameter to achieve a target project IRR using iterative optimization.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 Tab 2: Summary</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Executive overview of project financials</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Key Metrics Displayed:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Project IRR, NPV, and payback periods</li>
                                        <li>Capital IRR and NPV (investor perspective)</li>
                                        <li>LCOE and average cost per kWh</li>
                                        <li>Total project investment and financing summary</li>
                                        <li>EVA totals</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Critical Insight:</strong> Distinguishes between project-level returns (independent of financing) and capital returns (equity investor perspective).</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💰 Tab 3: Investment & Financing</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Capital requirements and funding structure analysis</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Components:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Total investment breakdown by category</li>
                                        <li>Financing structure (debt vs. equity)</li>
                                        <li>Construction loan analysis</li>
                                        <li>Working capital requirements</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Key Relationships:</strong> Shows how investment per MW scales with capacity and how equity percentage affects loan requirements.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📅 Tab 4: Loan Repayment</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Debt service schedule and cash flow analysis</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Features:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Annual principal and interest payments</li>
                                        <li>Remaining loan balance tracking</li>
                                        <li>Total debt service over project life</li>
                                        <li>Cumulative payments analysis</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Cash Flow Impact:</strong> Loan payments directly reduce available cash for distribution to equity investors.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💵 Tab 5: Total Cost & Cost/kWh</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Operating cost analysis and unit energy costs</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Cost Categories:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Operating & maintenance costs</li>
                                        <li>Land lease costs</li>
                                        <li>Depreciation (non-cash)</li>
                                        <li>Interest on debt</li>
                                        <li>Total cost per kWh analysis</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Key Insight:</strong> Separates cash and non-cash costs, showing the evolution of cost structure over project life.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Tab 6: Profit & Loss</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Annual income statement analysis</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>P&L Components:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Operating income from energy sales</li>
                                        <li>VAT and surtax calculations</li>
                                        <li>Operating expenses</li>
                                        <li>Depreciation and interest</li>
                                        <li>Income tax calculations</li>
                                        <li>Profit distribution and reserves</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Reserve Management:</strong> Models statutory and discretionary reserves as per corporate governance requirements.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚖️ Tab 7: Balance Sheet</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Financial position and asset management</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Balance Sheet Items:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Fixed assets (net of depreciation)</li>
                                        <li>Current assets (primarily cash)</li>
                                        <li>Long-term debt</li>
                                        <li>Equity and accumulated profits</li>
                                        <li>Balance verification</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Financial Health:</strong> Tracks asset base erosion through depreciation and cash generation from operations.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💎 Tab 8: EVA</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Value creation analysis beyond accounting profits</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>EVA Analysis:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Net Operating Profit After Tax (NOPAT)</li>
                                        <li>Capital employed calculations</li>
                                        <li>Cost of capital application</li>
                                        <li>Annual value creation/destruction</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Strategic Insight:</strong> EVA measures whether the project creates value above the cost of capital, providing a superior metric to accounting profit alone.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 Tab 9: Charts</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Visual analysis and trend identification</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Chart Types:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Cash flow trends over time</li>
                                        <li>Cost breakdown analysis</li>
                                        <li>Profitability trends</li>
                                        <li>Financial ratio evolution</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Analytical Value:</strong> Visual representation helps identify inflection points, trends, and potential issues in project performance.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'relationships':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🔗 Input-Output Relationship Matrix</h2>
                        
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🎯 Primary Impact Relationships</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 dark:border-gray-600">
                                            <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-100">Input Parameter</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-100">Direct Impact</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-100">Secondary Effects</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                        <tr>
                                            <td className="py-3 px-4 font-medium text-blue-600 dark:text-blue-400">installedCapacity</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Total Investment, Annual Generation</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">All revenue metrics, financing requirements</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-medium text-blue-600 dark:text-blue-400">onGridTariff</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Operating Income</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Total Profit, Income Tax, all return metrics</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-medium text-blue-600 dark:text-blue-400">longTermLoanRate</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Interest Payments</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Cash flows, profitability, all return metrics</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-medium text-blue-600 dark:text-blue-400">discountRate</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">NPV calculations, EVA</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Project viability assessment</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Revenue Dependencies</h3>
                                <div className="space-y-3">
                                    {[
                                        { param: 'Installed Capacity', impact: 'Scales all generation', multiplier: 'Linear' },
                                        { param: 'Annual Hours', impact: 'Capacity factor effect', multiplier: 'Linear' },
                                        { param: 'Grid Tariff', impact: 'Revenue per kWh', multiplier: 'Linear' },
                                        { param: 'Degradation Rate', impact: 'Long-term revenue', multiplier: 'Exponential decline' }
                                    ].map((item, index) => (
                                        <div key={index} className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                            <span className="font-medium text-blue-800 dark:text-blue-200">{item.param}</span>
                                            <div className="text-right">
                                                <div className="text-sm text-blue-600 dark:text-blue-400">{item.impact}</div>
                                                <div className="text-xs text-blue-500 dark:text-blue-300">{item.multiplier}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💰 Cost Dependencies</h3>
                                <div className="space-y-3">
                                    {[
                                        { param: 'Investment per MW', impact: 'Capital costs', multiplier: 'Linear' },
                                        { param: 'Operating Cost Rate', impact: 'Annual O&M', multiplier: 'Percentage of revenue' },
                                        { param: 'Land Cost per Acre', impact: 'Ongoing land costs', multiplier: 'Linear' },
                                        { param: 'Cost Escalation', impact: 'Future cost growth', multiplier: 'Compound growth' }
                                    ].map((item, index) => (
                                        <div key={index} className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                            <span className="font-medium text-green-800 dark:text-green-200">{item.param}</span>
                                            <div className="text-right">
                                                <div className="text-sm text-green-600 dark:text-green-400">{item.impact}</div>
                                                <div className="text-xs text-green-500 dark:text-green-300">{item.multiplier}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'assumptions':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🏗️ Model Assumptions & Limitations</h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🔧 Engineering Assumptions</h3>
                                <div className="space-y-3">
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-blue-600 dark:text-blue-400">1.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Linear Degradation</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">PV degradation follows a constant annual rate</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-blue-600 dark:text-blue-400">2.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Capacity Factor</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Assumes consistent annual full-load hours</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-blue-600 dark:text-blue-400">3.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Land Requirements</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Fixed 5 acres per MW ratio</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-blue-600 dark:text-blue-400">4.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Technology</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Standard crystalline silicon PV technology</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-blue-600 dark:text-blue-400">5.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Grid Connection</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Assumes stable grid access and no curtailment</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💰 Financial Assumptions</h3>
                                <div className="space-y-3">
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-green-600 dark:text-green-400">1.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Constant Tariff</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">No escalation in electricity selling price</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-green-600 dark:text-green-400">2.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Inflation</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Only operating costs inflate; revenues remain constant</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-green-600 dark:text-green-400">3.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Tax Stability</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Tax rates remain constant throughout project life</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-green-600 dark:text-green-400">4.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Perfect Markets</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">No transaction costs or market frictions</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-green-600 dark:text-green-400">5.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Debt Service</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Straight-line principal repayment (not annuity)</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 Economic Assumptions</h3>
                                <div className="space-y-3">
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-orange-600 dark:text-orange-400">1.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Discount Rate</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Remains constant (no term structure)</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-orange-600 dark:text-orange-400">2.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Currency</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">All calculations in nominal USD terms</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-orange-600 dark:text-orange-400">3.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Political Risk</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Assumes stable regulatory environment</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-orange-600 dark:text-orange-400">4.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Environmental</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">No carbon pricing or environmental costs/benefits</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-orange-600 dark:text-orange-400">5.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Maintenance</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">O&M costs scale with revenue, not actual maintenance needs</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📋 Accounting Assumptions</h3>
                                <div className="space-y-3">
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-purple-600 dark:text-purple-400">1.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Depreciation</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Straight-line method over 25 years</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-purple-600 dark:text-purple-400">2.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Working Capital</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Fixed 2% of total investment</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-purple-600 dark:text-purple-400">3.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Construction</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Single-year construction period</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-purple-600 dark:text-purple-400">4.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Reserves</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Statutory and discretionary reserves as per corporate law</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-purple-600 dark:text-purple-400">5.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Cash Management</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">No specific cash management strategies modeled</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6">
                            <h3 className="text-xl font-bold text-yellow-800 dark:text-yellow-200 mb-4">⚠️ Important Limitations</h3>
                            <p className="text-yellow-700 dark:text-yellow-300 mb-4">
                                These assumptions simplify the complex reality of solar project development and operation. 
                                Users should consider these limitations when interpreting results and making investment decisions.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-yellow-800 dark:text-yellow-200">Model Simplifications:</h4>
                                    <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                                        <li>• No weather variability modeling</li>
                                        <li>• Simplified O&M cost structure</li>
                                        <li>• No equipment failure scenarios</li>
                                        <li>• Single financing structure</li>
                                    </ul>
                                </div>
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-yellow-800 dark:text-yellow-200">Real-World Considerations:</h4>
                                    <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                                        <li>• Regulatory changes</li>
                                        <li>• Technology improvements</li>
                                        <li>• Market price volatility</li>
                                        <li>• Force majeure events</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'kpis':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🎯 Key Performance Indicators (KPIs)</h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Investment Viability Metrics</h3>
                                <div className="space-y-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-blue-800 dark:text-blue-200">Project IRR &gt; Discount Rate</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">Indicates project viability</p>
                                        <div className="mt-2 text-lg font-bold text-blue-600 dark:text-blue-400">
                                            {formatPercentage(outputs.indicators?.projectIRR)} vs {formatPercentage(inputs.discountRate)}
                                        </div>
                                        <div className={`text-sm mt-1 ${(outputs.indicators?.projectIRR || 0) > inputs.discountRate ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {(outputs.indicators?.projectIRR || 0) > inputs.discountRate ? '✅ VIABLE' : '❌ NOT VIABLE'}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-green-800 dark:text-green-200">Positive Project NPV</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">Confirms value creation</p>
                                        <div className="mt-2 text-lg font-bold text-green-600 dark:text-green-400">
                                            {formatCurrency(outputs.indicators?.projectNPV)}
                                        </div>
                                        <div className={`text-sm mt-1 ${(outputs.indicators?.projectNPV || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {(outputs.indicators?.projectNPV || 0) > 0 ? '✅ POSITIVE' : '❌ NEGATIVE'}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-orange-800 dark:text-orange-200">LCOE &lt; Market Price</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">Ensures competitive positioning</p>
                                        <div className="mt-2 text-lg font-bold text-orange-600 dark:text-orange-400">
                                            {formatCurrency(outputs.indicators?.lcoe, 4)}/kWh
                                        </div>
                                    </div>
                                    
                                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-purple-800 dark:text-purple-200">Payback Period &lt; 10 years</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">Acceptable risk profile</p>
                                        <div className="mt-2 text-lg font-bold text-purple-600 dark:text-purple-400">
                                            {formatYears(outputs.indicators?.paybackPeriod)}
                                        </div>
                                        <div className={`text-sm mt-1 ${(outputs.indicators?.paybackPeriod || 0) < 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {(outputs.indicators?.paybackPeriod || 0) < 10 ? '✅ ACCEPTABLE' : '❌ TOO LONG'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💰 Investor Return Metrics</h3>
                                <div className="space-y-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-blue-800 dark:text-blue-200">Capital IRR &gt; Target Return</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">Adequate equity returns</p>
                                        <div className="mt-2 text-lg font-bold text-blue-600 dark:text-blue-400">
                                            {formatPercentage(outputs.indicators?.capitalIRR)}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-green-800 dark:text-green-200">Positive Capital NPV</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">Equity value creation</p>
                                        <div className="mt-2 text-lg font-bold text-green-600 dark:text-green-400">
                                            {formatCurrency(outputs.indicators?.capitalNPV)}
                                        </div>
                                        <div className={`text-sm mt-1 ${(outputs.indicators?.capitalNPV || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {(outputs.indicators?.capitalNPV || 0) > 0 ? '✅ POSITIVE' : '❌ NEGATIVE'}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-orange-800 dark:text-orange-200">Stable Cash Distributions</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">Predictable investor returns</p>
                                        <div className="mt-2 text-lg font-bold text-orange-600 dark:text-orange-400">
                                            {formatPercentage(inputs.profitDistributionRate)} of profit
                                        </div>
                                    </div>
                                    
                                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-purple-800 dark:text-purple-200">Positive EVA</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">Value creation above cost of capital</p>
                                        <div className="mt-2 text-lg font-bold text-purple-600 dark:text-purple-400">
                                            {formatCurrency(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0)}
                                        </div>
                                        <div className={`text-sm mt-1 ${(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0) > 0 ? '✅ CREATING VALUE' : '❌ DESTROYING VALUE'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🏥 Financial Health Metrics</h3>
                                <div className="space-y-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-blue-800 dark:text-blue-200">Debt Service Coverage &gt; 1.2</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">Adequate cash flow for debt service</p>
                                        <div className="mt-2 text-sm text-blue-600 dark:text-blue-400">
                                            Calculated from annual cash flows
                                        </div>
                                    </div>
                                    
                                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-green-800 dark:text-green-200">Operating Margin &gt; 20%</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">Healthy operational efficiency</p>
                                        <div className="mt-2 text-lg font-bold text-green-600 dark:text-green-400">
                                            {formatPercentage(1 - inputs.operatingCostRate)}
                                        </div>
                                        <div className={`text-sm mt-1 ${(1 - inputs.operatingCostRate) > 0.2 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {(1 - inputs.operatingCostRate) > 0.2 ? '✅ HEALTHY' : '❌ LOW MARGIN'}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-orange-800 dark:text-orange-200">Cost Escalation &lt; 3%</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">Controlled cost growth</p>
                                        <div className="mt-2 text-lg font-bold text-orange-600 dark:text-orange-400">
                                            {formatPercentage(inputs.operatingCostEscalationRate)}
                                        </div>
                                        <div className={`text-sm mt-1 ${inputs.operatingCostEscalationRate < 0.03 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {inputs.operatingCostEscalationRate < 0.03 ? '✅ CONTROLLED' : '❌ HIGH ESCALATION'}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-purple-800 dark:text-purple-200">Balance Sheet Balance = 0</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">Accounting accuracy verification</p>
                                        <div className="mt-2 text-sm text-purple-600 dark:text-purple-400">
                                            Verified in Balance Sheet tab
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'sensitivity':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🔄 Sensitivity Analysis & Investment Decision Framework</h2>
                        
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-6 rounded-lg border border-blue-200 dark:border-blue-700">
                            <h3 className="text-xl font-bold text-blue-800 dark:text-blue-200 mb-4">💡 Purpose & Strategic Value</h3>
                            <p className="text-blue-700 dark:text-blue-300 mb-4">
                                Sensitivity analysis is the cornerstone of prudent solar investment decision-making. It reveals how changes in key assumptions impact project returns, 
                                helping investors understand risk exposure and identify the most critical success factors.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">🎯 Risk Assessment</h4>
                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">Quantify downside scenarios and probability of losses</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 dark:text-purple-200">📊 Scenario Planning</h4>
                                    <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">Model best-case, worst-case, and most-likely outcomes</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-800 dark:text-green-200">🔧 Optimization</h4>
                                    <p className="text-sm text-green-600 dark:text-green-400 mt-2">Identify which parameters to focus on for maximum impact</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🎯 Critical Success Factors (Ranked by Impact)</h3>
                                <div className="space-y-4">
                                    <div className="border-l-4 border-red-500 pl-4 bg-red-50 dark:bg-red-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-red-800 dark:text-red-200">1. Electricity Tariff (On-Grid Price)</h4>
                                        <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                                            <strong>Impact:</strong> Direct linear effect on all revenue streams
                                        </p>
                                        <p className="text-xs text-red-600 dark:text-red-400">
                                            <strong>Current Value:</strong> ${formatCurrency(inputs.onGridTariff, 4)}/kWh | 
                                            <strong> Risk:</strong> Regulatory changes, market competition | 
                                            <strong> Mitigation:</strong> Long-term power purchase agreements (PPAs)
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-orange-500 pl-4 bg-orange-50 dark:bg-orange-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-orange-800 dark:text-orange-200">2. Capital Investment Cost ($/MW)</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mb-2">
                                            <strong>Impact:</strong> Direct effect on initial investment, debt requirements, and depreciation
                                        </p>
                                        <p className="text-xs text-orange-600 dark:text-orange-400">
                                            <strong>Current Value:</strong> ${formatCurrency(inputs.investmentPerMW, 0)}/MW | 
                                            <strong> Risk:</strong> Technology costs, supply chain, labor | 
                                            <strong> Mitigation:</strong> Fixed-price EPC contracts, hedging strategies
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-yellow-500 pl-4 bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-yellow-800 dark:text-yellow-200">3. Financing Cost (Interest Rate)</h4>
                                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
                                            <strong>Impact:</strong> Exponential effect on debt service and equity returns
                                        </p>
                                        <p className="text-xs text-yellow-600 dark:text-yellow-400">
                                            <strong>Current Value:</strong> {formatPercentage(inputs.longTermLoanRate)} | 
                                            <strong> Risk:</strong> Interest rate cycles, credit rating changes | 
                                            <strong> Mitigation:</strong> Fixed-rate financing, interest rate swaps
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-green-500 pl-4 bg-green-50 dark:bg-green-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-green-800 dark:text-green-200">4. Capacity Factor (Annual Hours)</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mb-2">
                                            <strong>Impact:</strong> Direct effect on energy generation and revenue
                                        </p>
                                        <p className="text-xs text-green-600 dark:text-green-400">
                                            <strong>Current Value:</strong> {inputs.annualHours.toLocaleString()} hours | 
                                            <strong> Risk:</strong> Weather patterns, equipment performance | 
                                            <strong> Mitigation:</strong> Conservative estimates, performance monitoring
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-blue-500 pl-4 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-blue-800 dark:text-blue-200">5. Operating Costs (% of Revenue)</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                                            <strong>Impact:</strong> Direct effect on margins and cash flows
                                        </p>
                                        <p className="text-xs text-blue-600 dark:text-blue-400">
                                            <strong>Current Value:</strong> {formatPercentage(inputs.operatingCostRate)} | 
                                            <strong> Risk:</strong> Inflation, maintenance complexity | 
                                            <strong> Mitigation:</strong> Service contracts, predictive maintenance
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚡ Parameter Interactions & Compound Effects</h3>
                                <div className="space-y-4">
                                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-purple-800 dark:text-purple-200">🔄 Revenue Amplification Chain</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-2">
                                            <strong>Capacity ↗️ × Hours ↗️ × Tariff ↗️ = Revenue³</strong> (cubic growth effect)
                                        </p>
                                        <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                            Example: 10% increase in each = {formatPercentage(1.1*1.1*1.1-1)} total revenue increase
                                        </p>
                                    </div>
                                    
                                    <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-red-800 dark:text-red-200">⚠️ Leverage Effect on Returns</h4>
                                        <p className="text-sm text-red-700 dark:text-red-300 mt-2">
                                            <strong>Interest Rate ↗️ + Investment Cost ↗️ = Equity IRR ↘️↘️</strong> (amplified negative impact)
                                        </p>
                                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                            Higher leverage means small revenue/cost changes create large equity return swings
                                        </p>
                                    </div>
                                    
                                    <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-amber-800 dark:text-amber-200">📉 Degradation Compounding</h4>
                                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-2">
                                            <strong>PV Degradation × Time = Exponential Revenue Loss</strong>
                                        </p>
                                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                            Current {formatPercentage(inputs.pvDegradationRate)}/year = {formatPercentage(Math.pow(1-inputs.pvDegradationRate, 25)-1)} total loss over 25 years
                                        </p>
                                    </div>
                                    
                                    <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-teal-800 dark:text-teal-200">💰 Tax Shield Optimization</h4>
                                        <p className="text-sm text-teal-700 dark:text-teal-300 mt-2">
                                            <strong>Depreciation + Interest = Tax Savings</strong>
                                        </p>
                                        <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">
                                            Higher debt ratio increases tax benefits but also increases financial risk
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🎯 Investment Decision Framework</h3>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div className="space-y-4">
                                    <h4 className="font-bold text-green-800 dark:text-green-200">✅ GO/NO-GO Criteria</h4>
                                    <div className="space-y-3">
                                        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-green-700 dark:text-green-300">Base Case Requirements</h5>
                                            <ul className="text-sm text-green-600 dark:text-green-400 mt-1 space-y-1">
                                                <li>• Project IRR &gt; {formatPercentage(inputs.discountRate + 0.03)} (300bp premium)</li>
                                                <li>• Capital IRR &gt; {formatPercentage(inputs.discountRate + 0.05)} (500bp premium)</li>
                                                <li>• Payback &lt; 8 years</li>
                                                <li>• Positive NPV in all scenarios</li>
                                            </ul>
                                        </div>
                                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-yellow-700 dark:text-yellow-300">Stress Test Survival</h5>
                                            <ul className="text-sm text-yellow-600 dark:text-yellow-400 mt-1 space-y-1">
                                                <li>• -20% tariff scenario: IRR &gt; discount rate</li>
                                                <li>• +30% capex scenario: Still profitable</li>
                                                <li>• +200bp financing rate: Acceptable returns</li>
                                                <li>• -15% generation: Positive cash flows</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-bold text-blue-800 dark:text-blue-200">📊 Risk Assessment Matrix</h4>
                                    <div className="space-y-3">
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-blue-700 dark:text-blue-300">High Impact / High Probability</h5>
                                            <ul className="text-sm text-blue-600 dark:text-blue-400 mt-1 space-y-1">
                                                <li>• Electricity price volatility</li>
                                                <li>• Equipment cost inflation</li>
                                                <li>• Regulatory changes</li>
                                            </ul>
                                        </div>
                                        <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-orange-700 dark:text-orange-300">High Impact / Low Probability</h5>
                                            <ul className="text-sm text-orange-600 dark:text-orange-400 mt-1 space-y-1">
                                                <li>• Major policy shifts</li>
                                                <li>• Technology disruption</li>
                                                <li>• Force majeure events</li>
                                            </ul>
                                        </div>
                                        <div className="bg-gray-50 dark:bg-gray-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-gray-700 dark:text-gray-300">Monitor Continuously</h5>
                                            <ul className="text-sm text-gray-600 dark:text-gray-400 mt-1 space-y-1">
                                                <li>• Performance degradation</li>
                                                <li>• Financing market conditions</li>
                                                <li>• Grid stability & curtailment</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-bold text-purple-800 dark:text-purple-200">🔧 Optimization Strategies</h4>
                                    <div className="space-y-3">
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-purple-700 dark:text-purple-300">Revenue Enhancement</h5>
                                            <ul className="text-sm text-purple-600 dark:text-purple-400 mt-1 space-y-1">
                                                <li>• Negotiate long-term PPAs</li>
                                                <li>• Optimize plant location</li>
                                                <li>• Premium equipment selection</li>
                                            </ul>
                                        </div>
                                        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-indigo-700 dark:text-indigo-300">Cost Optimization</h5>
                                            <ul className="text-sm text-indigo-600 dark:text-indigo-400 mt-1 space-y-1">
                                                <li>• Competitive EPC bidding</li>
                                                <li>• Efficient financing structure</li>
                                                <li>• Performance-based O&M</li>
                                            </ul>
                                        </div>
                                        <div className="bg-pink-50 dark:bg-pink-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-pink-700 dark:text-pink-300">Risk Mitigation</h5>
                                            <ul className="text-sm text-pink-600 dark:text-pink-400 mt-1 space-y-1">
                                                <li>• Insurance coverage</li>
                                                <li>• Performance guarantees</li>
                                                <li>• Hedging strategies</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-900/50 dark:to-blue-900/50 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Recommended Analysis Workflow</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <div className="text-center">
                                        <span className="text-2xl">1️⃣</span>
                                        <h4 className="font-bold text-gray-800 dark:text-gray-100 mt-2">Base Case</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                            Establish realistic assumptions. Current model shows:
                                        </p>
                                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                                            Project IRR: {formatPercentage(outputs.indicators?.projectIRR)}<br/>
                                            Capital IRR: {formatPercentage(outputs.indicators?.capitalIRR)}<br/>
                                            NPV: {formatCurrency(outputs.indicators?.projectNPV, 0)}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <div className="text-center">
                                        <span className="text-2xl">2️⃣</span>
                                        <h4 className="font-bold text-gray-800 dark:text-gray-100 mt-2">Single Variable</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                            Test each critical factor ±20%
                                        </p>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            • Tariff: ±$0.01/kWh<br/>
                                            • Capex: ±$200k/MW<br/>
                                            • Interest: ±2%<br/>
                                            • Hours: ±300/year
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <div className="text-center">
                                        <span className="text-2xl">3️⃣</span>
                                        <h4 className="font-bold text-gray-800 dark:text-gray-100 mt-2">Multi-Variable</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                            Create 3×3 scenario matrices
                                        </p>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            • Tariff vs Capex<br/>
                                            • Interest vs Hours<br/>
                                            • Opex vs Degradation<br/>
                                            • Financing vs Equity
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <div className="text-center">
                                        <span className="text-2xl">4️⃣</span>
                                        <h4 className="font-bold text-gray-800 dark:text-gray-100 mt-2">Monte Carlo</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                            Probability distributions for key variables
                                        </p>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            • 1000+ simulations<br/>
                                            • P10/P50/P90 outcomes<br/>
                                            • Risk metrics<br/>
                                            • Correlation effects
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mt-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-blue-200 dark:border-blue-600">
                                <h4 className="font-bold text-blue-800 dark:text-blue-200 mb-2">💡 Pro Tip: Focus on Controllable vs. Market Factors</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <h5 className="font-semibold text-green-700 dark:text-green-300">✅ Controllable (Optimize These)</h5>
                                        <ul className="text-green-600 dark:text-green-400 mt-1 space-y-1">
                                            <li>• Equipment selection & cost</li>
                                            <li>• Financing structure & terms</li>
                                            <li>• Site selection & design</li>
                                            <li>• O&M strategy & costs</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <h5 className="font-semibold text-orange-700 dark:text-orange-300">⚠️ Market-Driven (Hedge/Mitigate)</h5>
                                        <ul className="text-orange-600 dark:text-orange-400 mt-1 space-y-1">
                                            <li>• Electricity prices & tariffs</li>
                                            <li>• Interest rates & credit markets</li>
                                            <li>• Regulatory environment</li>
                                            <li>• Weather & resource variability</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'implementation':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🛠️ Technical Implementation</h2>
                        
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 p-6 rounded-lg border border-purple-200 dark:border-purple-700">
                            <h3 className="text-xl font-bold text-purple-800 dark:text-purple-200 mb-4">🏗️ System Architecture</h3>
                            <p className="text-purple-700 dark:text-purple-300 mb-4">
                                The Solar Financial Model is built on a modern React.js foundation with a modular, scalable architecture that separates concerns 
                                between data calculation, state management, and user interface components.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 dark:text-purple-200">⚛️ Frontend</h4>
                                    <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">React 18+ with functional components and hooks</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">🧮 Engine</h4>
                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">Pure JavaScript calculation engine with real-time updates</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-800 dark:text-green-200">📊 Visualization</h4>
                                    <p className="text-sm text-green-600 dark:text-green-400 mt-2">Recharts library for interactive charts and graphs</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🔧 Core Components</h3>
                                <div className="space-y-4">
                                    <div className="border-l-4 border-blue-500 pl-4 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-blue-800 dark:text-blue-200">Input Management</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                            Real-time input validation, controlled components with useState hooks, and automatic recalculation triggers
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-green-500 pl-4 bg-green-50 dark:bg-green-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-green-800 dark:text-green-200">Calculation Engine</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                                            Modular financial functions with dependency tracking, memoized calculations using useMemo for performance
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-purple-500 pl-4 bg-purple-50 dark:bg-purple-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-purple-800 dark:text-purple-200">UI Components</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                                            Responsive grid layouts, tabbed interface, modal dialogs, and dark/light theme support
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-orange-500 pl-4 bg-orange-50 dark:bg-orange-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-orange-800 dark:text-orange-200">Data Visualization</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                                            Interactive charts with Recharts, responsive design, tooltips, and legend management
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚡ Performance Features</h3>
                                <div className="space-y-4">
                                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-yellow-800 dark:text-yellow-200">Memoization Strategy</h4>
                                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-2">
                                            <strong>useMemo:</strong> Complex calculations cached based on input dependencies<br/>
                                            <strong>useCallback:</strong> Event handlers optimized to prevent unnecessary re-renders<br/>
                                            <strong>React.memo:</strong> Component-level memoization for expensive UI components
                                        </p>
                                    </div>
                                    
                                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-green-800 dark:text-green-200">Real-time Updates</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-2">
                                            <strong>Instant Recalculation:</strong> All financial metrics update immediately on input change<br/>
                                            <strong>Dependency Tracking:</strong> Only affected calculations are re-computed<br/>
                                            <strong>State Synchronization:</strong> Consistent data across all tabs and components
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 Data Flow Architecture</h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                    <span className="text-3xl block mb-2">📝</span>
                                    <h4 className="font-bold text-blue-800 dark:text-blue-200">Input Layer</h4>
                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">User inputs with validation</p>
                                </div>
                                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                    <span className="text-3xl block mb-2">⚙️</span>
                                    <h4 className="font-bold text-green-800 dark:text-green-200">Calculation Engine</h4>
                                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">Financial computations</p>
                                </div>
                                <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                                    <span className="text-3xl block mb-2">📋</span>
                                    <h4 className="font-bold text-purple-800 dark:text-purple-200">Data Processing</h4>
                                    <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">Results formatting</p>
                                </div>
                                <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                                    <span className="text-3xl block mb-2">📊</span>
                                    <h4 className="font-bold text-orange-800 dark:text-orange-200">Presentation Layer</h4>
                                    <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">Tables, charts, reports</p>
                                </div>
                            </div>
                            
                            <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                                <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">🔄 Calculation Dependencies</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                                    The system uses a dependency graph to ensure calculations are performed in the correct order:
                                </p>
                                <div className="text-xs text-gray-600 dark:text-gray-400 font-mono bg-white dark:bg-gray-800 p-3 rounded border">
                                    inputs → investmentAndFinancing → energyGeneration → costStructure → loanRepayment → 
                                    profitAndLoss → balanceSheet → cashFlows → indicators → evaStatement
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'usage':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">📚 Usage Guidelines</h2>
                        
                        <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 p-6 rounded-lg border border-green-200 dark:border-green-700">
                            <h3 className="text-xl font-bold text-green-800 dark:text-green-200 mb-4">🎯 Target Users & Use Cases</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">💼 Financial Analysts</h4>
                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">Investment evaluation, due diligence, risk assessment</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-800 dark:text-green-200">🏗️ Project Developers</h4>
                                    <p className="text-sm text-green-600 dark:text-green-400 mt-2">Feasibility studies, financing optimization, project planning</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 dark:text-purple-200">💰 Investors</h4>
                                    <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">Return analysis, portfolio evaluation, decision support</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🚀 Getting Started Workflow</h3>
                                <div className="space-y-4">
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-blue-600 dark:text-blue-400 text-lg">1.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Project Configuration</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Start with Basic Data tab. Enter project size (MW), construction timeline, and operational period.</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-green-600 dark:text-green-400 text-lg">2.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Investment Parameters</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Set capital costs ($/MW), equipment specifications, and initial funding structure.</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-purple-600 dark:text-purple-400 text-lg">3.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Revenue & Cost Inputs</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Configure electricity tariff, capacity factor, O&M costs, and degradation rates.</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-orange-600 dark:text-orange-400 text-lg">4.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Financing Structure</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Define loan terms, interest rates, and equity requirements.</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-red-600 dark:text-red-400 text-lg">5.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Results Analysis</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Review Summary tab, analyze financial statements, and assess viability.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚡ Best Practices</h3>
                                <div className="space-y-4">
                                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                        <h4 className="font-bold text-green-800 dark:text-green-200">✅ Conservative Assumptions</h4>
                                        <ul className="text-sm text-green-700 dark:text-green-300 mt-2 space-y-1">
                                            <li>• Use P75-P90 capacity factors for risk management</li>
                                            <li>• Apply higher degradation rates (0.5-0.7% annually)</li>
                                            <li>• Include contingency in capital costs (5-10%)</li>
                                        </ul>
                                    </div>
                                    
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                        <h4 className="font-bold text-blue-800 dark:text-blue-200">📊 Sensitivity Testing</h4>
                                        <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1">
                                            <li>• Test key variables ±20% from base case</li>
                                            <li>• Focus on controllable vs. market factors</li>
                                            <li>• Document scenarios and stress tests</li>
                                        </ul>
                                    </div>
                                    
                                    <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
                                        <h4 className="font-bold text-orange-800 dark:text-orange-200">🔍 Results Validation</h4>
                                        <ul className="text-sm text-orange-700 dark:text-orange-300 mt-2 space-y-1">
                                            <li>• Cross-check balance sheet balancing</li>
                                            <li>• Verify cash flow consistency</li>
                                            <li>• Compare against industry benchmarks</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Advanced Analysis Techniques</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3">🎯 Goal Seek Analysis</h4>
                                    <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-blue-700 dark:text-blue-300">Target IRR Optimization</h5>
                                            <p>Use Goal Seek to find required equity amount for desired return rate</p>
                                        </div>
                                        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-green-700 dark:text-green-300">Break-even Analysis</h5>
                                            <p>Determine minimum tariff or maximum cost for project viability</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h4 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-3">📊 Scenario Planning</h4>
                                    <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-purple-700 dark:text-purple-300">Three-Point Estimates</h5>
                                            <p>Model optimistic, base case, and pessimistic scenarios</p>
                                        </div>
                                        <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-orange-700 dark:text-orange-300">Stress Testing</h5>
                                            <p>Test extreme conditions: recession, rate hikes, technology disruption</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🔧 Troubleshooting & Common Issues</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2">❌ Common Errors</h4>
                                    <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>• <strong>Negative NPV:</strong> Check tariff rates and cost assumptions</li>
                                        <li>• <strong>Low IRR:</strong> Review financing structure and leverage</li>
                                        <li>• <strong>Unrealistic Payback:</strong> Validate operating cost percentages</li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">✅ Quick Fixes</h4>
                                    <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>• <strong>Balance Sheet Issues:</strong> Check depreciation and loan balances</li>
                                        <li>• <strong>Cash Flow Problems:</strong> Verify interest calculations</li>
                                        <li>• <strong>Tax Anomalies:</strong> Review tax rate settings</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'extensions':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🔮 Model Extensions & Future Enhancements</h2>
                        
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 p-6 rounded-lg border border-indigo-200 dark:border-indigo-700">
                            <h3 className="text-xl font-bold text-indigo-800 dark:text-indigo-200 mb-4">🚀 Roadmap Overview</h3>
                            <p className="text-indigo-700 dark:text-indigo-300 mb-4">
                                The Solar Financial Model is designed with extensibility in mind. Future enhancements will focus on advanced analytics, 
                                integration capabilities, and emerging technologies in the renewable energy sector.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-indigo-800 dark:text-indigo-200">📊 Analytics</h4>
                                    <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-2">Advanced modeling and AI-powered insights</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 dark:text-purple-200">🔗 Integration</h4>
                                    <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">External data sources and API connectivity</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">⚡ Technology</h4>
                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">Emerging solar technologies and market trends</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Advanced Analytics Features</h3>
                                <div className="space-y-4">
                                    <div className="border-l-4 border-blue-500 pl-4 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-blue-800 dark:text-blue-200">🎲 Monte Carlo Simulation</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                            Probabilistic modeling with 1000+ scenarios, risk distributions, and confidence intervals for NPV/IRR
                                        </p>
                                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                                            <strong>Status:</strong> Planned for v2.0 | <strong>Impact:</strong> Enhanced risk assessment
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-green-500 pl-4 bg-green-50 dark:bg-green-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-green-800 dark:text-green-200">🤖 Machine Learning Optimization</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                                            AI-powered parameter optimization for maximum IRR, automatic scenario generation, and predictive analytics
                                        </p>
                                        <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                                            <strong>Status:</strong> Research phase | <strong>Impact:</strong> Automated optimization
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-purple-500 pl-4 bg-purple-50 dark:bg-purple-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-purple-800 dark:text-purple-200">📊 Portfolio Analysis</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                                            Multi-project portfolio optimization, correlation analysis, and diversification benefits assessment
                                        </p>
                                        <p className="text-xs text-purple-600 dark:text-purple-400 mt-2">
                                            <strong>Status:</strong> Conceptual | <strong>Impact:</strong> Portfolio-level insights
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-orange-500 pl-4 bg-orange-50 dark:bg-orange-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-orange-800 dark:text-orange-200">🌡️ Climate Risk Modeling</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                                            Weather pattern analysis, climate change impact on generation, and extreme weather risk assessment
                                        </p>
                                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-2">
                                            <strong>Status:</strong> Planned for v2.5 | <strong>Impact:</strong> Climate-aware projections
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🔗 Integration & Connectivity</h3>
                                <div className="space-y-4">
                                    <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-cyan-800 dark:text-cyan-200">🌐 Market Data APIs</h4>
                                        <p className="text-sm text-cyan-700 dark:text-cyan-300 mt-2">
                                            <strong>Real-time Integration:</strong> Electricity prices, equipment costs, financing rates<br/>
                                            <strong>Data Sources:</strong> IRENA, NREL, Bloomberg New Energy Finance<br/>
                                            <strong>Update Frequency:</strong> Daily/weekly automated refreshes
                                        </p>
                                    </div>
                                    
                                    <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-teal-800 dark:text-teal-200">☁️ Weather Data Integration</h4>
                                        <p className="text-sm text-teal-700 dark:text-teal-300 mt-2">
                                            <strong>Solar Resource:</strong> Historical and forecast irradiation data<br/>
                                            <strong>Providers:</strong> NREL NSRDB, SolarGIS, Meteonorm<br/>
                                            <strong>Features:</strong> Site-specific analysis, P50/P90 estimates
                                        </p>
                                    </div>
                                    
                                    <div className="bg-pink-50 dark:bg-pink-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-pink-800 dark:text-pink-200">📋 ERP System Connectivity</h4>
                                        <p className="text-sm text-pink-700 dark:text-pink-300 mt-2">
                                            <strong>Export Capabilities:</strong> Excel, CSV, JSON, PDF reports<br/>
                                            <strong>Import Functions:</strong> Project templates, bulk data updates<br/>
                                            <strong>API Endpoints:</strong> RESTful API for system integration
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚡ Technology & Market Extensions</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div className="space-y-4">
                                    <h4 className="font-bold text-yellow-800 dark:text-yellow-200">🔋 Energy Storage Integration</h4>
                                    <div className="space-y-3">
                                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-yellow-700 dark:text-yellow-300">Battery Storage Modeling</h5>
                                            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                                                LCOE optimization, capacity sizing, degradation modeling
                                            </p>
                                        </div>
                                        <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-orange-700 dark:text-orange-300">Grid Services Revenue</h5>
                                            <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                                                Ancillary services, frequency regulation, peak shaving
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="space-y-4">
                                    <h4 className="font-bold text-green-800 dark:text-green-200">🌍 Carbon Market Integration</h4>
                                    <div className="space-y-3">
                                        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-green-700 dark:text-green-300">Carbon Credit Revenue</h5>
                                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                                Voluntary and compliance markets, pricing models
                                            </p>
                                        </div>
                                        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-emerald-700 dark:text-emerald-300">ESG Impact Metrics</h5>
                                            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                                                CO₂ reduction, sustainability scoring, impact reporting
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="space-y-4">
                                    <h4 className="font-bold text-purple-800 dark:text-purple-200">⚙️ Technology Evolution</h4>
                                    <div className="space-y-3">
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-purple-700 dark:text-purple-300">Bifacial & Tracking</h5>
                                            <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                                Advanced module technologies, tracking system benefits
                                            </p>
                                        </div>
                                        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-indigo-700 dark:text-indigo-300">Agrivoltaics Modeling</h5>
                                            <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                                                Dual land use, agricultural revenue integration
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-900/50 dark:to-blue-900/50 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🛣️ Development Timeline</h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <span className="text-2xl block mb-2">🎯</span>
                                    <h4 className="font-bold text-gray-800 dark:text-gray-100">Q2 2025</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Monte Carlo simulation</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">Market data APIs</p>
                                </div>
                                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <span className="text-2xl block mb-2">🔋</span>
                                    <h4 className="font-bold text-gray-800 dark:text-gray-100">Q4 2025</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Energy storage modeling</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">Portfolio analysis</p>
                                </div>
                                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <span className="text-2xl block mb-2">🤖</span>
                                    <h4 className="font-bold text-gray-800 dark:text-gray-100">Q2 2026</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">ML optimization</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">Climate risk modeling</p>
                                </div>
                                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <span className="text-2xl block mb-2">🌍</span>
                                    <h4 className="font-bold text-gray-800 dark:text-gray-100">2027+</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Carbon markets</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">Agrivoltaics support</p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'theory':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🔬 Financial Theory & Methodologies</h2>
                        
                        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 p-6 rounded-lg border border-teal-200 dark:border-teal-700">
                            <h3 className="text-xl font-bold text-teal-800 dark:text-teal-200 mb-4">🧮 Theoretical Foundation</h3>
                            <p className="text-teal-700 dark:text-teal-300 mb-4">
                                The Solar Financial Model is built upon established financial theories and industry best practices, 
                                ensuring academic rigor and professional standards in renewable energy project evaluation.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-teal-800 dark:text-teal-200">💰 DCF Theory</h4>
                                    <p className="text-sm text-teal-600 dark:text-teal-400 mt-2">Discounted cash flow fundamentals and time value of money</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-cyan-800 dark:text-cyan-200">📊 Capital Structure</h4>
                                    <p className="text-sm text-cyan-600 dark:text-cyan-400 mt-2">Optimal debt-equity mix and cost of capital theory</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">⚖️ Risk Assessment</h4>
                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">Modern portfolio theory and risk-return optimization</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💰 Discounted Cash Flow (DCF) Theory</h3>
                                <div className="space-y-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-blue-800 dark:text-blue-200">🎯 Core Principle</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                                            The intrinsic value of a project equals the present value of its expected future cash flows, 
                                            discounted at an appropriate risk-adjusted rate that reflects the project's systematic risk.
                                        </p>
                                    </div>
                                    
                                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-green-800 dark:text-green-200">📐 Mathematical Foundation</h4>
                                        <div className="text-sm text-green-700 dark:text-green-300 mt-2 space-y-2">
                                            <div className="font-mono bg-white dark:bg-gray-800 p-2 rounded text-xs">
                                                NPV = Σ(CFₜ / (1 + r)ᵗ) - I₀
                                            </div>
                                            <p><strong>Where:</strong> CFₜ = Cash flow in period t, r = Discount rate, I₀ = Initial investment</p>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-purple-800 dark:text-purple-200">🔄 IRR Methodology</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-2">
                                            Internal Rate of Return represents the discount rate that makes NPV = 0, calculated using 
                                            Newton-Raphson iteration method for numerical precision in multi-period cash flows.
                                        </p>
                                    </div>
                                    
                                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-orange-800 dark:text-orange-200">⏱️ Time Value Considerations</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-2">
                                            Accounts for inflation, opportunity cost, and investment risk through the discount rate, 
                                            with different rates for project vs. equity cash flows reflecting leverage effects.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 Capital Structure Theory</h3>
                                <div className="space-y-4">
                                    <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-indigo-800 dark:text-indigo-200">⚖️ Modigliani-Miller Framework</h4>
                                        <p className="text-sm text-indigo-700 dark:text-indigo-300 mt-2">
                                            In perfect markets, capital structure doesn't affect firm value. However, in practice, 
                                            tax benefits of debt and financial distress costs create an optimal leverage point.
                                        </p>
                                    </div>
                                    
                                    <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-cyan-800 dark:text-cyan-200">🛡️ Tax Shield Benefits</h4>
                                        <div className="text-sm text-cyan-700 dark:text-cyan-300 mt-2 space-y-2">
                                            <div className="font-mono bg-white dark:bg-gray-800 p-2 rounded text-xs">
                                                Tax Shield = Interest Expense × Tax Rate
                                            </div>
                                            <p>Debt financing creates tax deductions that increase project value</p>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-teal-800 dark:text-teal-200">💡 WACC Optimization</h4>
                                        <p className="text-sm text-teal-700 dark:text-teal-300 mt-2">
                                            Weighted Average Cost of Capital minimizes at optimal debt-equity ratio, 
                                            balancing tax benefits against increased financial risk and cost of financial distress.
                                        </p>
                                    </div>
                                    
                                    <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-emerald-800 dark:text-emerald-200">📈 Leverage Effects</h4>
                                        <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-2">
                                            Financial leverage amplifies equity returns but also increases risk. 
                                            Solar projects typically support 70-80% debt ratios due to predictable cash flows.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚖️ Risk Assessment & Valuation Theory</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-3">📊 Modern Portfolio Theory (MPT)</h4>
                                    <div className="space-y-3">
                                        <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-red-700 dark:text-red-300">Risk-Return Optimization</h5>
                                            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                                Systematic risk (beta) determines required return; unsystematic risk can be diversified away
                                            </p>
                                        </div>
                                        <div className="bg-pink-50 dark:bg-pink-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-pink-700 dark:text-pink-300">Capital Asset Pricing Model</h5>
                                            <p className="text-xs text-pink-600 dark:text-pink-400 mt-1">
                                                Required return = Risk-free rate + Beta × Market risk premium
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3">🎯 Real Options Theory</h4>
                                    <div className="space-y-3">
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-blue-700 dark:text-blue-300">Expansion Options</h5>
                                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                                Value of ability to expand capacity if market conditions improve
                                            </p>
                                        </div>
                                        <div className="bg-sky-50 dark:bg-sky-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-sky-700 dark:text-sky-300">Abandonment Options</h5>
                                            <p className="text-xs text-sky-600 dark:text-sky-400 mt-1">
                                                Value of salvage option if project becomes uneconomical
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚡ Renewable Energy Finance Specifics</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <h4 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-3">🌞 Asset Characteristics</h4>
                                    <div className="space-y-3 text-sm">
                                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-yellow-700 dark:text-yellow-300">Predictable Cash Flows</h5>
                                            <p className="text-yellow-600 dark:text-yellow-400 mt-1">
                                                Solar irradiation patterns are well-understood, enabling accurate revenue forecasting
                                            </p>
                                        </div>
                                        <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-amber-700 dark:text-amber-300">Long Asset Life</h5>
                                            <p className="text-amber-600 dark:text-amber-400 mt-1">
                                                25+ year operational periods with predictable degradation patterns
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h4 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-3">💰 Revenue Models</h4>
                                    <div className="space-y-3 text-sm">
                                        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-green-700 dark:text-green-300">Power Purchase Agreements</h5>
                                            <p className="text-green-600 dark:text-green-400 mt-1">
                                                Long-term contracts provide revenue certainty and support project financing
                                            </p>
                                        </div>
                                        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-emerald-700 dark:text-emerald-300">Merchant Risk</h5>
                                            <p className="text-emerald-600 dark:text-emerald-400 mt-1">
                                                Exposure to spot electricity prices requires sophisticated risk management
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h4 className="text-lg font-semibold text-purple-800 dark:text-purple-200 mb-3">🔄 Economic Value Added</h4>
                                    <div className="space-y-3 text-sm">
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-purple-700 dark:text-purple-300">Value Creation Measurement</h5>
                                            <p className="text-purple-600 dark:text-purple-400 mt-1">
                                                EVA = NOPAT - (Capital × WACC) measures true economic profit
                                            </p>
                                        </div>
                                        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-indigo-700 dark:text-indigo-300">Stakeholder Value</h5>
                                            <p className="text-indigo-600 dark:text-indigo-400 mt-1">
                                                Positive EVA indicates value creation above cost of capital
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📚 Academic References & Standards</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">📖 Core Financial Theory</h4>
                                    <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>• <strong>Brealey, Myers & Allen:</strong> Principles of Corporate Finance</li>
                                        <li>• <strong>Damodaran:</strong> Investment Valuation & Corporate Finance</li>
                                        <li>• <strong>Ross, Westerfield & Jaffe:</strong> Modern Financial Management</li>
                                        <li>• <strong>Copeland, Koller & Murrin:</strong> Valuation Methods</li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">⚡ Renewable Energy Finance</h4>
                                    <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>• <strong>IRENA:</strong> Renewable Energy Finance Guidelines</li>
                                        <li>• <strong>IEA:</strong> Energy Technology Investment Standards</li>
                                        <li>• <strong>NREL:</strong> Solar Project Financial Modeling Best Practices</li>
                                        <li>• <strong>Bloomberg NEF:</strong> Clean Energy Investment Methodologies</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'about':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">👨‍💼 About This Model</h2>
                        
                        <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 p-8 rounded-xl border border-blue-200 dark:border-blue-700">
                            <div className="text-center">
                                <div className="mb-6">
                                    <span className="text-6xl">⚡</span>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
                                    Solar Financial Model
                                </h3>
                                <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                                    A comprehensive financial analysis tool for utility-scale solar energy projects
                                </p>
                                
                                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-600 shadow-lg">
                                    <div className="flex items-center justify-center space-x-3 mb-4">
                                        <span className="text-2xl">👨‍💼</span>
                                        <div className="text-left">
                                            <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100">Created by</h4>
                                            <p className="text-lg text-blue-600 dark:text-blue-400 font-semibold">Fernando Izeta</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-center space-x-3">
                                        <span className="text-2xl">📅</span>
                                        <div className="text-left">
                                            <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100">Date</h4>
                                            <p className="text-lg text-green-600 dark:text-green-400 font-semibold">July 12th, 2025</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div className="text-center">
                                    <span className="text-3xl mb-3 block">🎯</span>
                                    <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3">Purpose</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        Provide comprehensive financial analysis and decision-making tools for solar energy investments
                                    </p>
                                </div>
                            </div>
                            
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div className="text-center">
                                    <span className="text-3xl mb-3 block">⚡</span>
                                    <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3">Technology</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        Built with React, featuring interactive calculations, real-time updates, and comprehensive reporting
                                    </p>
                                </div>
                            </div>
                            
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div className="text-center">
                                    <span className="text-3xl mb-3 block">📊</span>
                                    <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3">Features</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        9 analysis tabs, sensitivity analysis, goal seek functionality, and exportable documentation
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🌟 Model Highlights</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3">Financial Analysis</h4>
                                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                                        <li>• Complete DCF analysis with NPV and IRR calculations</li>
                                        <li>• Detailed P&L, balance sheet, and cash flow projections</li>
                                        <li>• Economic Value Added (EVA) analysis</li>
                                        <li>• Comprehensive cost modeling including degradation</li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-3">Decision Support</h4>
                                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                                        <li>• Sensitivity analysis for key variables</li>
                                        <li>• Goal seek optimization for target returns</li>
                                        <li>• Risk assessment and scenario planning</li>
                                        <li>• Investment decision framework</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="text-center">
                                <p className="text-gray-600 dark:text-gray-400 text-sm">
                                    This model represents a comprehensive approach to solar project financial analysis, 
                                    combining industry best practices with advanced financial modeling techniques.
                                </p>
                                <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
                                    <p className="text-xs text-gray-500 dark:text-gray-500">
                                        Version 1.0 | Solar Financial Model | © 2025 Fernando Izeta
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">📚 Additional Documentation</h2>
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <p className="text-gray-600 dark:text-gray-300">
                                Select a section from the navigation menu to view detailed documentation.
                            </p>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="flex flex-col lg:flex-row gap-6">
            {/* Navigation Sidebar */}
            <div className="lg:w-1/4">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sticky top-4">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">📖 Documentation Sections</h3>
                    <nav className="space-y-2">
                        {menuItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => setActiveSection(item.id)}
                                className={`w-full text-left px-3 py-2 rounded-lg transition-colors duration-200 ${
                                    activeSection === item.id
                                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                            >
                                <span className="text-sm">{item.icon}</span> {item.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Main Content */}
            <div className="lg:w-3/4">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    {renderSection()}
                </div>
            </div>
        </div>
    );
};

const ChartsTab = ({ outputs, isDarkMode }) => {
    const pnlChartData = useMemo(() => outputs.pnlStatement?.map(p => ({name: `Year ${p.year}`, 'Operating Income': p.operatingIncome, 'Net Profit': p.netProfit, 'Total Profit': p.totalProfit})), [outputs.pnlStatement]);
    const costChartData = useMemo(() => outputs.costStatement?.map(c => ({name: `Year ${c.year}`, 'Operating Cost': c.operatingCost, 'Land Cost': c.landCost, 'Depreciation': c.depreciation, 'Interest': c.interest})), [outputs.costStatement]);
    const tickColor = isDarkMode ? '#A0AEC0' : '#4A5568';
    return (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Financial Charts</h2><div className="grid lg:grid-cols-2 gap-8">
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg h-96"><h3 className="font-semibold mb-4">Profitability Over Time</h3><ResponsiveContainer width="100%" height="100%"><LineChart data={pnlChartData}><CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#4A5568' : '#E2E8F0'} /><XAxis dataKey="name" tick={{ fill: tickColor }} /><YAxis tickFormatter={(v) => new Intl.NumberFormat('en-US', { notation: "compact", compactDisplay: "short" }).format(v)} tick={{ fill: tickColor }}/><Tooltip formatter={(value) => formatCurrency(value, 0)} contentStyle={{ backgroundColor: isDarkMode ? '#2D3748' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#4A5568' : '#E2E8F0'}` }} /><Legend wrapperStyle={{color: tickColor}} /><Line type="monotone" dataKey="Operating Income" stroke="#3b82f6" /><Line type="monotone" dataKey="Net Profit" stroke="#16a34a" /></LineChart></ResponsiveContainer></div>
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg h-96"><h3 className="font-semibold mb-4">Cost Structure Over Time</h3><ResponsiveContainer width="100%" height="100%"><BarChart data={costChartData}><CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#4A5568' : '#E2E8F0'} /><XAxis dataKey="name" tick={{ fill: tickColor }}/><YAxis tickFormatter={(v) => new Intl.NumberFormat('en-US', { notation: "compact", compactDisplay: "short" }).format(v)} tick={{ fill: tickColor }}/><Tooltip formatter={(value) => formatCurrency(value, 0)} contentStyle={{ backgroundColor: isDarkMode ? '#2D3748' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#4A5568' : '#E2E8F0'}` }} /><Legend wrapperStyle={{color: tickColor}} /><Bar dataKey="Operating Cost" stackId="a" fill="#8884d8" /><Bar dataKey="Land Cost" stackId="a" fill="#ff7c7c" /><Bar dataKey="Depreciation" stackId="a" fill="#82ca9d" /><Bar dataKey="Interest" stackId="a" fill="#ffc658" /></BarChart></ResponsiveContainer></div>
    </div>
    <Explanation title="How This Works">
        <p>This tab provides a visual representation of the key data from the financial statements, making it easier to spot trends and understand relationships.</p>
        <p><strong>Profitability Over Time:</strong> This line chart shows the project's revenue (Operating Income) versus its Net Profit over the entire operational period. It helps to quickly assess the overall profitability trend and when the project becomes most profitable.</p>
        <p><strong>Cost Structure Over Time:</strong> This stacked bar chart breaks down the total annual costs into their main components (Operating Costs, Depreciation, and Interest). It is useful for understanding how the cost composition changes over the project's lifetime, such as when interest costs decrease as the loan is paid down.</p>
    </Explanation>
    </div>);
};
